/**
 * myCargoCFS - Warehouse Management System
 * Main Dashboard & Operations Hub
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { API_URL } from "../config/api";
import {
  Truck,
  Package,
  Camera,
  AlertTriangle,
  CheckCircle,
  Clock,
  MapPin,
  Calendar,
  Plus,
  Search,
  RefreshCw,
  ArrowRight,
  ArrowLeft,
  Loader2,
  X,
  ChevronRight,
  Users,
  BarChart3,
  Warehouse,
  ClipboardList,
  Eye,
  TrendingUp,
  Target,
  Phone,
  MessageSquare,
  Send,
  GripVertical,
  FileText,
  Ship,
  Box,
  Mail,
  Edit,
  Trash2,
  Upload,
  FileUp,
  Sparkles,
  Menu,
  Settings,
  LogOut,
  Smartphone,
  Monitor,
  Lock,
  Unlock,
  StickyNote,
  Bell,
  BellRing,
  Anchor,
  ExternalLink,
  Key,
  Shield,
  PanelRightOpen,
  PanelRightClose,
  Link2,
  Pin,
  PinOff,
  Image,
  Info,
  ChevronDown,
  Check,
  ArrowDownToLine,
  ArrowUpFromLine,
  ArrowLeftRight,
  Layers,
} from "lucide-react";


// ==========================================
// AUTHENTICATION CONTEXT
// ==========================================

const useAuth = () => {
  const [staff, setStaff] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("wms_token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      verifyToken();
    } else {
      setLoading(false);
    }
  }, []);

  const verifyToken = async () => {
    try {
      const response = await axios.get(`${API_URL}/wms/staff/verify-token?token=${token}`);
      if (response.data.valid) {
        setStaff(response.data.staff);
      } else {
        logout();
      }
    } catch (error) {
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = (newToken, staffInfo) => {
    localStorage.setItem("wms_token", newToken);
    setToken(newToken);
    setStaff(staffInfo);
  };

  const logout = () => {
    localStorage.removeItem("wms_token");
    setToken(null);
    setStaff(null);
  };

  return { staff, token, loading, login, logout, isAuthenticated: !!staff };
};

// ==========================================
// MAIN WMS PAGE
// ==========================================

export default function WMSPage() {
  const auth = useAuth();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [showBurgerMenu, setShowBurgerMenu] = useState(false);
  const [showNavMenu, setShowNavMenu] = useState(false);
  const [showTasksPane, setShowTasksPane] = useState(false);
  const [tasksPaneLocked, setTasksPaneLocked] = useState(false);
  const [showNotesPanel, setShowNotesPanel] = useState(false);
  const [reminders, setReminders] = useState([]);
  const [showReminderPopup, setShowReminderPopup] = useState(false);

  useEffect(() => {
    if (auth.isAuthenticated) {
      fetchDashboard();
      fetchReminders();
      // Check reminders every 30 seconds
      const interval = setInterval(fetchReminders, 30000);
      return () => clearInterval(interval);
    } else {
      setLoading(false);
    }
  }, [auth.isAuthenticated]);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API_URL}/wms/dashboard`);
      setDashboard(response.data);
    } catch (error) {
      console.error("Failed to fetch dashboard", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchReminders = async () => {
    if (!auth.staff?.email) return;
    try {
      const response = await axios.get(`${API_URL}/wms/reminders?staff_email=${encodeURIComponent(auth.staff.email)}&unread_only=true`);
      const newReminders = response.data.reminders || [];
      if (newReminders.length > reminders.length) {
        setShowReminderPopup(true);
        // Auto-hide after 5 seconds
        setTimeout(() => setShowReminderPopup(false), 5000);
      }
      setReminders(newReminders);
    } catch (error) {
      console.error("Failed to fetch reminders", error);
    }
  };

  const tabs = [
    { id: "dashboard", label: "Dashboard", icon: BarChart3 },
    { id: "orders", label: "Orders", icon: ClipboardList },
    { id: "tasks", label: "Tasks", icon: CheckCircle },
    { id: "task-templates", label: "Templates", icon: ClipboardList },
    { id: "fcl-tracking", label: "FCL Track", icon: Anchor },
    { id: "bl-photos", label: "BL Photos", icon: Camera },
    { id: "warehouse-map", label: "Warehouse", icon: MapPin },
    { id: "performance", label: "Performance", icon: TrendingUp },
    { id: "schedule", label: "Schedule", icon: Calendar },
    { id: "pre-alerts", label: "Pre-Alerts", icon: Truck },
    { id: "operations", label: "Operations", icon: Package },
    { id: "inventory", label: "Inventory", icon: Warehouse },
  ];

  // Show login screen if not authenticated
  if (auth.loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!auth.isAuthenticated) {
    return <StaffLoginPage onLogin={auth.login} />;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-blue-900 to-blue-700 text-white shadow-lg sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                <Warehouse className="w-6 h-6" />
              </div>
              <div>
                <h1 className="text-xl font-bold">myCargoCFS</h1>
                <p className="text-xs text-blue-200">Warehouse Management System</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Notes Button */}
              <Button
                variant="ghost"
                size="sm"
                className="text-white hover:bg-white/10"
                onClick={() => setShowNotesPanel(!showNotesPanel)}
                data-testid="notes-btn"
              >
                <StickyNote className="w-4 h-4" />
              </Button>
              
              {/* Tasks Pane Toggle */}
              <Button
                variant="ghost"
                size="sm"
                className={`text-white hover:bg-white/10 ${showTasksPane ? "bg-white/20" : ""}`}
                onClick={() => setShowTasksPane(!showTasksPane)}
                data-testid="tasks-pane-btn"
              >
                {showTasksPane ? <PanelRightClose className="w-4 h-4" /> : <PanelRightOpen className="w-4 h-4" />}
              </Button>
              
              {/* Reminders Bell */}
              <div className="relative">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-white hover:bg-white/10"
                  onClick={() => setShowReminderPopup(!showReminderPopup)}
                  data-testid="reminders-btn"
                >
                  {reminders.length > 0 ? (
                    <BellRing className="w-4 h-4 text-yellow-300" />
                  ) : (
                    <Bell className="w-4 h-4" />
                  )}
                  {reminders.length > 0 && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-xs flex items-center justify-center">
                      {reminders.length}
                    </span>
                  )}
                </Button>
              </div>
              
              {/* Refresh */}
              <Button
                variant="ghost"
                size="sm"
                className="text-white hover:bg-white/10 hidden sm:flex"
                onClick={fetchDashboard}
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
              
              {/* Unified Burger Menu */}
              <div className="relative">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-white hover:bg-white/10"
                  onClick={() => setShowBurgerMenu(!showBurgerMenu)}
                  data-testid="burger-menu-btn"
                >
                  <Menu className="w-5 h-5" />
                </Button>
                
                {/* Unified Dropdown Menu */}
                <AnimatePresence>
                  {showBurgerMenu && (
                    <>
                      {/* Backdrop */}
                      <div 
                        className="fixed inset-0 z-40" 
                        onClick={() => setShowBurgerMenu(false)} 
                      />
                      
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="absolute right-0 top-full mt-2 w-72 bg-white rounded-xl shadow-2xl border z-50 overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {/* User Info */}
                        <div className="p-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white">
                          <p className="font-semibold">{auth.staff?.name || auth.staff?.email}</p>
                          <p className="text-sm text-blue-200">{auth.staff?.role || "Staff"}</p>
                        </div>
                        
                        {/* Navigation Section */}
                        <div className="py-2 border-b max-h-64 overflow-y-auto">
                          <p className="px-4 py-1 text-xs font-semibold text-slate-400 uppercase">Navigation</p>
                          {tabs.map((tab) => (
                            <button
                              key={tab.id}
                              onClick={() => {
                                setActiveTab(tab.id);
                                setShowBurgerMenu(false);
                              }}
                              className={`flex items-center gap-3 w-full px-4 py-2.5 text-left transition-colors ${
                                activeTab === tab.id
                                  ? "bg-blue-50 text-blue-700"
                                  : "text-slate-700 hover:bg-slate-50"
                              }`}
                              data-testid={`nav-${tab.id}`}
                            >
                              <tab.icon className={`w-4 h-4 ${activeTab === tab.id ? "text-blue-600" : "text-slate-400"}`} />
                              <span className="font-medium text-sm">{tab.label}</span>
                              {activeTab === tab.id && (
                                <Check className="w-4 h-4 ml-auto text-blue-600" />
                              )}
                            </button>
                          ))}
                        </div>
                        
                        {/* Admin Section */}
                        <div className="py-2 border-b">
                          <p className="px-4 py-1 text-xs font-semibold text-slate-400 uppercase">Views</p>
                          <a
                            href="/wms/customer"
                            target="_blank"
                            className="flex items-center gap-3 px-4 py-2 hover:bg-slate-50 text-slate-700"
                          >
                            <Monitor className="w-4 h-4 text-slate-400" />
                            <span className="text-sm">Customer Portal</span>
                            <ExternalLink className="w-3 h-3 ml-auto text-slate-400" />
                          </a>
                          <a
                            href="/wms/customer?mobile=true"
                            target="_blank"
                            className="flex items-center gap-3 px-4 py-2 hover:bg-slate-50 text-slate-700"
                          >
                            <Smartphone className="w-4 h-4 text-slate-400" />
                            <span className="text-sm">Customer Mobile</span>
                            <ExternalLink className="w-3 h-3 ml-auto text-slate-400" />
                          </a>
                          <a
                            href="/wms/app?mobile=true"
                            target="_blank"
                            className="flex items-center gap-3 px-4 py-2 hover:bg-slate-50 text-slate-700"
                          >
                            <Smartphone className="w-4 h-4 text-slate-400" />
                            <span className="text-sm">Staff Mobile</span>
                            <ExternalLink className="w-3 h-3 ml-auto text-slate-400" />
                          </a>
                        </div>
                        
                        {/* Logout */}
                        <div className="p-2">
                          <button
                            onClick={() => {
                              auth.logout();
                              setShowBurgerMenu(false);
                            }}
                            className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-red-50 text-red-600 w-full"
                          >
                            <LogOut className="w-4 h-4" />
                            <span className="text-sm font-medium">Logout</span>
                          </button>
                        </div>
                      </motion.div>
                    </>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Reminder Popup */}
      <AnimatePresence>
        {showReminderPopup && reminders.length > 0 && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            className="fixed top-20 right-4 w-80 bg-white rounded-lg shadow-xl border z-50"
          >
            <div className="p-3 border-b flex items-center justify-between bg-yellow-50">
              <div className="flex items-center gap-2">
                <BellRing className="w-5 h-5 text-yellow-600" />
                <span className="font-medium">Reminders</span>
              </div>
              <button onClick={() => setShowReminderPopup(false)}>
                <X className="w-4 h-4 text-slate-400" />
              </button>
            </div>
            <div className="max-h-64 overflow-y-auto">
              {reminders.slice(0, 5).map((reminder) => (
                <ReminderItem key={reminder.id} reminder={reminder} onDismiss={fetchReminders} />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content with Optional Tasks Pane */}
      <div className="flex">
        {/* Main Content */}
        <main className={`flex-1 max-w-7xl mx-auto px-4 py-6 transition-all ${showTasksPane ? "mr-80" : ""}`}>
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : (
            <>
              {activeTab === "dashboard" && <DashboardTab dashboard={dashboard} onRefresh={fetchDashboard} />}
              {activeTab === "orders" && <OrdersTab />}
              {activeTab === "tasks" && <TasksTab />}
              {activeTab === "task-templates" && <TaskTemplatesTab staffEmail={auth.staff?.email} />}
              {activeTab === "fcl-tracking" && <FCLTrackingTab staffEmail={auth.staff?.email} />}
              {activeTab === "bl-photos" && <BLPhotosTab staffEmail={auth.staff?.email} />}
              {activeTab === "warehouse-map" && <WarehouseMapTab staffEmail={auth.staff?.email} />}
              {activeTab === "performance" && <PerformanceTab />}
              {activeTab === "schedule" && <ScheduleTab />}
              {activeTab === "pre-alerts" && <PreAlertsTab />}
              {activeTab === "operations" && <OperationsTab />}
              {activeTab === "inventory" && <InventoryTab />}
            </>
          )}
        </main>

        {/* Tasks Reading Pane */}
        <AnimatePresence>
          {showTasksPane && (
            <TasksReadingPane
              locked={tasksPaneLocked}
              onToggleLock={() => setTasksPaneLocked(!tasksPaneLocked)}
              onClose={() => !tasksPaneLocked && setShowTasksPane(false)}
            />
          )}
        </AnimatePresence>
      </div>

      {/* Post-it Notes Panel */}
      <AnimatePresence>
        {showNotesPanel && (
          <NotesPanel
            staffEmail={auth.staff?.email}
            onClose={() => setShowNotesPanel(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// ==========================================
// STAFF LOGIN PAGE
// ==========================================

function StaffLoginPage({ onLogin }) {
  const [step, setStep] = useState("login"); // login, 2fa, forgot, reset
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [sessionToken, setSessionToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [debugCode, setDebugCode] = useState("");
  const [debugResetToken, setDebugResetToken] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Please enter email and password");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/wms/staff/login`, { email, password });
      if (response.data.requires_2fa) {
        setSessionToken(response.data.session_token);
        setDebugCode(response.data._debug_code || "");
        setStep("2fa");
        toast.success("2FA code sent to your email");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    if (!code) {
      toast.error("Please enter the 2FA code");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/wms/staff/verify-2fa`, {
        email,
        code,
        session_token: sessionToken,
      });
      if (response.data.success) {
        onLogin(response.data.token, response.data.staff);
        toast.success("Login successful!");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid code");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/wms/staff/forgot-password`, { email });
      setDebugResetToken(response.data._debug_token || "");
      setStep("reset");
      toast.success("Reset link sent to your email");
    } catch (error) {
      toast.error("Failed to send reset email");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!resetToken || !newPassword) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/wms/staff/reset-password`, {
        email,
        reset_token: resetToken,
        new_password: newPassword,
      });
      toast.success("Password reset successfully! Please login.");
      setStep("login");
      setPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Reset failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-slate-900 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6 text-white">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <Warehouse className="w-7 h-7" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">myCargoCFS</h1>
              <p className="text-blue-200 text-sm">Staff Portal</p>
            </div>
          </div>
        </div>

        {/* Login Form */}
        {step === "login" && (
          <form onSubmit={handleLogin} className="p-6 space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Email</label>
              <div className="relative">
                <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@cargo-cfs.com"
                  className="pl-10"
                  data-testid="login-email"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Password</label>
              <div className="relative">
                <Key className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-10"
                  data-testid="login-password"
                />
              </div>
            </div>
            <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Shield className="w-4 h-4 mr-2" />}
              Sign In
            </Button>
            <button
              type="button"
              onClick={() => setStep("forgot")}
              className="text-sm text-blue-600 hover:underline w-full text-center"
            >
              Forgot password?
            </button>
          </form>
        )}

        {/* 2FA Verification */}
        {step === "2fa" && (
          <form onSubmit={handleVerify2FA} className="p-6 space-y-4">
            <div className="text-center mb-4">
              <Shield className="w-12 h-12 mx-auto text-blue-600 mb-2" />
              <h2 className="text-lg font-semibold">Two-Factor Authentication</h2>
              <p className="text-sm text-slate-500">Enter the 6-digit code sent to {email}</p>
            </div>
            {debugCode && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-center">
                <p className="text-xs text-yellow-700">MOCKED: Your code is</p>
                <p className="text-2xl font-mono font-bold text-yellow-800">{debugCode}</p>
              </div>
            )}
            <Input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="123456"
              className="text-center text-2xl tracking-widest"
              maxLength={6}
              data-testid="2fa-code"
            />
            <Button type="submit" className="w-full" disabled={loading} data-testid="2fa-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              Verify Code
            </Button>
            <button
              type="button"
              onClick={() => setStep("login")}
              className="text-sm text-slate-500 hover:underline w-full text-center"
            >
              Back to login
            </button>
          </form>
        )}

        {/* Forgot Password */}
        {step === "forgot" && (
          <form onSubmit={handleForgotPassword} className="p-6 space-y-4">
            <div className="text-center mb-4">
              <Key className="w-12 h-12 mx-auto text-blue-600 mb-2" />
              <h2 className="text-lg font-semibold">Reset Password</h2>
              <p className="text-sm text-slate-500">Enter your email to receive a reset link</p>
            </div>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@cargo-cfs.com"
              data-testid="forgot-email"
            />
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              Send Reset Link
            </Button>
            <button
              type="button"
              onClick={() => setStep("login")}
              className="text-sm text-slate-500 hover:underline w-full text-center"
            >
              Back to login
            </button>
          </form>
        )}

        {/* Reset Password */}
        {step === "reset" && (
          <form onSubmit={handleResetPassword} className="p-6 space-y-4">
            <div className="text-center mb-4">
              <Key className="w-12 h-12 mx-auto text-blue-600 mb-2" />
              <h2 className="text-lg font-semibold">Set New Password</h2>
            </div>
            {debugResetToken && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-center">
                <p className="text-xs text-yellow-700">MOCKED: Your reset token is</p>
                <p className="text-xs font-mono text-yellow-800 break-all">{debugResetToken}</p>
              </div>
            )}
            <Input
              type="text"
              value={resetToken}
              onChange={(e) => setResetToken(e.target.value)}
              placeholder="Reset token from email"
              data-testid="reset-token"
            />
            <Input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="New password"
              data-testid="reset-password"
            />
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              Reset Password
            </Button>
            <button
              type="button"
              onClick={() => setStep("login")}
              className="text-sm text-slate-500 hover:underline w-full text-center"
            >
              Back to login
            </button>
          </form>
        )}
      </motion.div>
    </div>
  );
}

// ==========================================
// REMINDER ITEM
// ==========================================

function ReminderItem({ reminder, onDismiss }) {
  const handleDismiss = async () => {
    try {
      await axios.put(`${API_URL}/wms/reminders/${reminder.id}/read`);
      onDismiss();
    } catch (error) {
      console.error("Failed to dismiss reminder", error);
    }
  };

  return (
    <div className="p-3 border-b hover:bg-slate-50">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="font-medium text-sm">{reminder.title}</p>
          <p className="text-xs text-slate-500 line-clamp-2">{reminder.content}</p>
          {reminder.pf_link && (
            <a href={`/position-folders?id=${reminder.pf_link}`} className="text-xs text-blue-600 hover:underline flex items-center gap-1 mt-1">
              <Link2 className="w-3 h-3" />
              View PF
            </a>
          )}
        </div>
        <button onClick={handleDismiss} className="text-slate-400 hover:text-slate-600">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// ==========================================
// TASKS READING PANE
// ==========================================

function TasksReadingPane({ locked, onToggleLock, onClose }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");

  useEffect(() => {
    fetchTasks();
  }, [filter]);

  const fetchTasks = async () => {
    try {
      const response = await axios.get(`${API_URL}/wms/tasks/all?status=${filter}`);
      let allTasks = response.data.tasks || [];
      setTasks(allTasks);
    } catch (error) {
      console.error("Failed to fetch tasks", error);
    } finally {
      setLoading(false);
    }
  };

  const toggleTask = async (taskId, orderId) => {
    try {
      await axios.put(`${API_URL}/wms/orders/${orderId}/tasks/${taskId}/toggle`);
      fetchTasks();
    } catch (error) {
      toast.error("Failed to update task");
    }
  };

  return (
    <motion.div
      initial={{ x: 320, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: 320, opacity: 0 }}
      className="fixed right-0 top-0 h-screen w-80 bg-white border-l shadow-xl z-20 flex flex-col"
    >
      {/* Header */}
      <div className="p-4 border-b bg-slate-50 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ClipboardList className="w-5 h-5 text-blue-600" />
          <h3 className="font-semibold">Tasks</h3>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={onToggleLock}
            className={`p-1.5 rounded hover:bg-slate-200 ${locked ? "text-blue-600" : "text-slate-400"}`}
            title={locked ? "Unlock pane" : "Lock pane"}
          >
            {locked ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
          </button>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-slate-200 text-slate-400">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="p-2 border-b flex gap-1">
        {["all", "pending", "completed"].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-3 py-1 rounded text-xs font-medium capitalize ${
              filter === f ? "bg-blue-100 text-blue-700" : "text-slate-600 hover:bg-slate-100"
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      {/* Task List */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
          </div>
        ) : tasks.length === 0 ? (
          <div className="text-center py-10 text-slate-500">
            <ClipboardList className="w-10 h-10 mx-auto mb-2 opacity-30" />
            <p>No tasks found</p>
          </div>
        ) : (
          <div className="divide-y">
            {tasks.map((task) => (
              <div key={task.id} className="p-3 hover:bg-slate-50">
                <div className="flex items-start gap-2">
                  <button
                    onClick={() => toggleTask(task.id, task.order_id)}
                    className={`mt-0.5 w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 ${
                      task.completed ? "bg-green-500 border-green-500 text-white" : "border-slate-300"
                    }`}
                  >
                    {task.completed && <CheckCircle className="w-3 h-3" />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium ${task.completed ? "line-through text-slate-400" : ""}`}>
                      {task.label}
                    </p>
                    <p className="text-xs text-slate-500 truncate">{task.order_number}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
}

// ==========================================
// NOTES PANEL (POST-IT NOTES)
// ==========================================

const NOTE_COLORS = [
  { id: "yellow", bg: "bg-yellow-100", border: "border-yellow-300" },
  { id: "blue", bg: "bg-blue-100", border: "border-blue-300" },
  { id: "green", bg: "bg-green-100", border: "border-green-300" },
  { id: "pink", bg: "bg-pink-100", border: "border-pink-300" },
  { id: "purple", bg: "bg-purple-100", border: "border-purple-300" },
];

function NotesPanel({ staffEmail, onClose }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewNote, setShowNewNote] = useState(false);
  const [editingNote, setEditingNote] = useState(null);

  useEffect(() => {
    fetchNotes();
  }, []);

  const fetchNotes = async () => {
    try {
      const response = await axios.get(`${API_URL}/wms/notes?staff_email=${encodeURIComponent(staffEmail)}`);
      setNotes(response.data.notes || []);
    } catch (error) {
      console.error("Failed to fetch notes", error);
    } finally {
      setLoading(false);
    }
  };

  const deleteNote = async (noteId) => {
    if (!confirm("Delete this note?")) return;
    try {
      await axios.delete(`${API_URL}/wms/notes/${noteId}`);
      fetchNotes();
      toast.success("Note deleted");
    } catch (error) {
      toast.error("Failed to delete note");
    }
  };

  const togglePin = async (note) => {
    try {
      await axios.put(`${API_URL}/wms/notes/${note.id}?pinned=${!note.pinned}`);
      fetchNotes();
    } catch (error) {
      toast.error("Failed to update note");
    }
  };

  const getColorClass = (colorId) => {
    const color = NOTE_COLORS.find(c => c.id === colorId) || NOTE_COLORS[0];
    return `${color.bg} ${color.border}`;
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 z-50 flex items-start justify-end p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ x: 100 }}
        animate={{ x: 0 }}
        exit={{ x: 100 }}
        className="bg-white rounded-xl w-full max-w-md h-[90vh] flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 border-b flex items-center justify-between bg-yellow-50">
          <div className="flex items-center gap-2">
            <StickyNote className="w-5 h-5 text-yellow-600" />
            <h3 className="font-semibold">Post-it Notes</h3>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={() => setShowNewNote(true)} data-testid="new-note-btn">
              <Plus className="w-4 h-4 mr-1" />
              New
            </Button>
            <button onClick={onClose}>
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>
        </div>

        {/* Notes Grid */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="w-6 h-6 animate-spin text-yellow-600" />
            </div>
          ) : notes.length === 0 ? (
            <div className="text-center py-10">
              <StickyNote className="w-12 h-12 mx-auto mb-2 text-slate-300" />
              <p className="text-slate-500">No notes yet</p>
              <Button size="sm" className="mt-4" onClick={() => setShowNewNote(true)}>
                <Plus className="w-4 h-4 mr-1" />
                Create First Note
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {notes.map((note) => (
                <motion.div
                  key={note.id}
                  layout
                  className={`p-3 rounded-lg border-2 ${getColorClass(note.color)} relative group`}
                >
                  {note.pinned && (
                    <Pin className="w-3 h-3 absolute top-2 right-2 text-slate-400" />
                  )}
                  <h4 className="font-medium text-sm mb-1 pr-4">{note.title}</h4>
                  <p className="text-xs text-slate-600 line-clamp-3">{note.content}</p>
                  {note.pf_link && (
                    <a href={`/position-folders?id=${note.pf_link}`} className="text-xs text-blue-600 hover:underline flex items-center gap-1 mt-2">
                      <Link2 className="w-3 h-3" />
                      Linked PF
                    </a>
                  )}
                  {note.reminder_at && !note.reminder_sent && (
                    <p className="text-xs text-orange-600 mt-1 flex items-center gap-1">
                      <Bell className="w-3 h-3" />
                      {new Date(note.reminder_at).toLocaleDateString()}
                    </p>
                  )}
                  {/* Actions */}
                  <div className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 flex gap-1">
                    <button onClick={() => togglePin(note)} className="p-1 bg-white rounded shadow">
                      {note.pinned ? <PinOff className="w-3 h-3" /> : <Pin className="w-3 h-3" />}
                    </button>
                    <button onClick={() => setEditingNote(note)} className="p-1 bg-white rounded shadow">
                      <Edit className="w-3 h-3" />
                    </button>
                    <button onClick={() => deleteNote(note.id)} className="p-1 bg-white rounded shadow text-red-500">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>

        {/* New Note Modal */}
        <AnimatePresence>
          {(showNewNote || editingNote) && (
            <NoteFormModal
              note={editingNote}
              staffEmail={staffEmail}
              onClose={() => { setShowNewNote(false); setEditingNote(null); }}
              onSuccess={() => {
                setShowNewNote(false);
                setEditingNote(null);
                fetchNotes();
              }}
            />
          )}
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
}

function NoteFormModal({ note, staffEmail, onClose, onSuccess }) {
  const [title, setTitle] = useState(note?.title || "");
  const [content, setContent] = useState(note?.content || "");
  const [color, setColor] = useState(note?.color || "yellow");
  const [pfLink, setPfLink] = useState(note?.pf_link || "");
  const [reminderAt, setReminderAt] = useState(note?.reminder_at?.split("T")[0] || "");
  const [reminderTime, setReminderTime] = useState(note?.reminder_at?.split("T")[1]?.slice(0, 5) || "09:00");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!title || !content) {
      toast.error("Please fill in title and content");
      return;
    }

    setSaving(true);
    try {
      const reminder = reminderAt ? `${reminderAt}T${reminderTime}:00Z` : null;

      if (note) {
        // Update
        await axios.put(`${API_URL}/wms/notes/${note.id}?title=${encodeURIComponent(title)}&content=${encodeURIComponent(content)}&color=${color}&pf_link=${pfLink || ""}&reminder_at=${reminder || ""}`);
      } else {
        // Create
        await axios.post(`${API_URL}/wms/notes`, {
          title,
          content,
          color,
          pf_link: pfLink || null,
          reminder_at: reminder,
          staff_email: staffEmail,
        });
      }
      toast.success(note ? "Note updated" : "Note created");
      onSuccess();
    } catch (error) {
      toast.error("Failed to save note");
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="absolute inset-0 bg-white rounded-xl flex flex-col"
    >
      <div className="p-4 border-b flex items-center justify-between">
        <h3 className="font-semibold">{note ? "Edit Note" : "New Note"}</h3>
        <button onClick={onClose}>
          <X className="w-5 h-5 text-slate-400" />
        </button>
      </div>

      <div className="flex-1 p-4 space-y-4 overflow-y-auto">
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Note title"
          data-testid="note-title"
        />
        <Textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="Note content..."
          rows={4}
          data-testid="note-content"
        />
        
        {/* Color Selection */}
        <div>
          <label className="text-sm font-medium text-slate-700 mb-2 block">Color</label>
          <div className="flex gap-2">
            {NOTE_COLORS.map((c) => (
              <button
                key={c.id}
                onClick={() => setColor(c.id)}
                className={`w-8 h-8 rounded-full ${c.bg} border-2 ${color === c.id ? "ring-2 ring-blue-500" : c.border}`}
              />
            ))}
          </div>
        </div>

        {/* PF Link */}
        <div>
          <label className="text-sm font-medium text-slate-700 mb-1 block">Link to PF (optional)</label>
          <Input
            value={pfLink}
            onChange={(e) => setPfLink(e.target.value)}
            placeholder="Position Folder ID"
          />
        </div>

        {/* Reminder */}
        <div>
          <label className="text-sm font-medium text-slate-700 mb-1 block">Reminder (optional)</label>
          <div className="flex gap-2">
            <Input
              type="date"
              value={reminderAt}
              onChange={(e) => setReminderAt(e.target.value)}
              className="flex-1"
            />
            <Input
              type="time"
              value={reminderTime}
              onChange={(e) => setReminderTime(e.target.value)}
              className="w-28"
            />
          </div>
        </div>
      </div>

      <div className="p-4 border-t flex gap-3">
        <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
        <Button onClick={handleSave} disabled={saving} className="flex-1" data-testid="save-note-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
          Save
        </Button>
      </div>
    </motion.div>
  );
}

// ==========================================
// FCL TRACKING TAB (STAFF)
// ==========================================

function FCLTrackingTab({ staffEmail }) {
  const [containerNumber, setContainerNumber] = useState("");
  const [trackingResult, setTrackingResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statuses, setStatuses] = useState([]);
  const [showUpdateForm, setShowUpdateForm] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [notes, setNotes] = useState("");
  const [pfId, setPfId] = useState("");
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    fetchStatuses();
  }, []);

  const fetchStatuses = async () => {
    try {
      const response = await axios.get(`${API_URL}/wms/fcl/statuses`);
      setStatuses(response.data.statuses || []);
    } catch (error) {
      console.error("Failed to fetch statuses", error);
    }
  };

  const handleSearch = async () => {
    if (!containerNumber.trim()) {
      toast.error("Please enter a container number");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/wms/fcl/tracking/${encodeURIComponent(containerNumber)}`);
      setTrackingResult(response.data);
      if (response.data.linked_pf?.id) {
        setPfId(response.data.linked_pf.id);
      }
    } catch (error) {
      toast.error("Failed to fetch tracking info");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async () => {
    if (!selectedStatus) {
      toast.error("Please select a status");
      return;
    }

    setUpdating(true);
    try {
      await axios.post(`${API_URL}/wms/fcl/update-status`, {
        container_number: containerNumber,
        pf_id: pfId || null,
        status: selectedStatus,
        notes: notes,
        updated_by: staffEmail,
      });
      toast.success("Status updated!");
      setShowUpdateForm(false);
      setNotes("");
      setSelectedStatus("");
      handleSearch(); // Refresh
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update status");
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl border p-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Anchor className="w-5 h-5 text-blue-600" />
          FCL Container Tracking - Port of Koper
        </h2>
        <p className="text-sm text-slate-500 mb-4">
          Track and update FCL container status. Updates sync with linked Position Folders.
        </p>

        <div className="flex gap-3">
          <div className="relative flex-1 max-w-md">
            <Anchor className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={containerNumber}
              onChange={(e) => setContainerNumber(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              placeholder="Container number (e.g., MSCU1234567)"
              className="pl-10"
              data-testid="fcl-staff-search"
            />
          </div>
          <Button onClick={handleSearch} disabled={loading}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            <span className="ml-2">Search</span>
          </Button>
        </div>
      </div>

      {/* Tracking Result */}
      {trackingResult && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl border overflow-hidden"
        >
          {/* Header */}
          <div className="p-4 border-b bg-gradient-to-r from-blue-50 to-slate-50">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold">{trackingResult.container_number}</h3>
                <p className="text-sm text-slate-500">Port of Koper, Slovenia</p>
              </div>
              <div className="flex items-center gap-3">
                {trackingResult.current_status_label && (
                  <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                    {trackingResult.current_status_label}
                  </span>
                )}
                <Button onClick={() => setShowUpdateForm(!showUpdateForm)} data-testid="fcl-update-btn">
                  <Edit className="w-4 h-4 mr-2" />
                  Update Status
                </Button>
              </div>
            </div>
          </div>

          {/* Update Form */}
          <AnimatePresence>
            {showUpdateForm && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="border-b bg-yellow-50 overflow-hidden"
              >
                <div className="p-4 space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium mb-1 block">New Status</label>
                      <select
                        value={selectedStatus}
                        onChange={(e) => setSelectedStatus(e.target.value)}
                        className="w-full h-10 border rounded-md px-3"
                        data-testid="fcl-status-select"
                      >
                        <option value="">Select status...</option>
                        {statuses.map((s) => (
                          <option key={s.id} value={s.id}>{s.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-1 block">Link to PF (optional)</label>
                      <Input
                        value={pfId}
                        onChange={(e) => setPfId(e.target.value)}
                        placeholder="Position Folder ID"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">Notes</label>
                    <Textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Add notes about this status update..."
                      rows={2}
                    />
                  </div>
                  <div className="flex gap-3">
                    <Button variant="outline" onClick={() => setShowUpdateForm(false)}>Cancel</Button>
                    <Button onClick={handleUpdateStatus} disabled={updating} data-testid="fcl-save-btn">
                      {updating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                      Save Update
                    </Button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Linked Info */}
          <div className="p-4 grid grid-cols-2 gap-4 border-b">
            {trackingResult.linked_pf && (
              <div className="p-3 bg-blue-50 rounded-lg">
                <p className="text-xs text-blue-600 font-medium">Linked Position Folder</p>
                <p className="font-bold">{trackingResult.linked_pf.folder_number}</p>
                <p className="text-sm text-slate-500">{trackingResult.linked_pf.customer_name}</p>
                <a href={`/position-folders?id=${trackingResult.linked_pf.id}`} className="text-xs text-blue-600 hover:underline flex items-center gap-1 mt-1">
                  <ExternalLink className="w-3 h-3" />
                  View in myEuropacific
                </a>
              </div>
            )}
            {trackingResult.linked_order && (
              <div className="p-3 bg-green-50 rounded-lg">
                <p className="text-xs text-green-600 font-medium">Linked WMS Order</p>
                <p className="font-bold">{trackingResult.linked_order.order_number}</p>
                <p className="text-sm text-slate-500">{trackingResult.linked_order.customer_name}</p>
              </div>
            )}
          </div>

          {/* Timeline */}
          <div className="p-4">
            <h4 className="font-semibold mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-blue-600" />
              Tracking History
            </h4>
            {trackingResult.events?.length > 0 ? (
              <div className="space-y-3">
                {trackingResult.events.map((event, idx) => (
                  <div key={event.id || idx} className="flex gap-3">
                    <div className="flex flex-col items-center">
                      <div className={`w-3 h-3 rounded-full ${idx === 0 ? "bg-blue-500" : "bg-slate-300"}`} />
                      {idx < trackingResult.events.length - 1 && <div className="w-0.5 flex-1 bg-slate-200" />}
                    </div>
                    <div className="flex-1 pb-4">
                      <p className="font-medium">{event.status_label}</p>
                      <p className="text-sm text-slate-500">{event.location}</p>
                      {event.notes && <p className="text-sm text-slate-400 italic">{event.notes}</p>}
                      <p className="text-xs text-slate-400 mt-1">
                        {new Date(event.timestamp).toLocaleString()} by {event.updated_by}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-center py-4">No tracking events yet</p>
            )}
          </div>
        </motion.div>
      )}
    </div>
  );
}

// ==========================================
// TASK TEMPLATES TAB
// ==========================================

const TASK_CATEGORIES = [
  { id: "inbound", label: "Inbound", color: "bg-blue-100 text-blue-700" },
  { id: "outbound", label: "Outbound", color: "bg-green-100 text-green-700" },
  { id: "handling", label: "Handling", color: "bg-purple-100 text-purple-700" },
  { id: "storage", label: "Storage", color: "bg-orange-100 text-orange-700" },
  { id: "customs", label: "Customs", color: "bg-red-100 text-red-700" },
  { id: "documentation", label: "Documentation", color: "bg-slate-100 text-slate-700" },
  { id: "communication", label: "Communication", color: "bg-cyan-100 text-cyan-700" },
  { id: "damage", label: "Damage", color: "bg-yellow-100 text-yellow-700" },
  { id: "custom", label: "Custom", color: "bg-pink-100 text-pink-700" },
];

function TaskTemplatesTab({ staffEmail }) {
  const [templates, setTemplates] = useState({ system_defaults: [], custom_templates: [] });
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newTemplate, setNewTemplate] = useState({ label: "", description: "", category: "custom" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API_URL}/wms/task-templates`);
      setTemplates(response.data);
    } catch (error) {
      console.error("Failed to fetch templates", error);
      toast.error("Failed to load task templates");
    } finally {
      setLoading(false);
    }
  };

  const handleAddTemplate = async () => {
    if (!newTemplate.label.trim()) {
      toast.error("Please enter a task name");
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API_URL}/wms/task-templates`, {
        ...newTemplate,
        created_by: staffEmail,
      });
      toast.success("Template created!");
      setShowAddForm(false);
      setNewTemplate({ label: "", description: "", category: "custom" });
      fetchTemplates();
    } catch (error) {
      toast.error("Failed to create template");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!confirm("Delete this template?")) return;

    try {
      await axios.delete(`${API_URL}/wms/task-templates/${templateId}`);
      toast.success("Template deleted");
      fetchTemplates();
    } catch (error) {
      toast.error("Failed to delete template");
    }
  };

  const getCategoryStyle = (categoryId) => {
    const cat = TASK_CATEGORIES.find((c) => c.id === categoryId);
    return cat?.color || "bg-slate-100 text-slate-700";
  };

  const getCategoryLabel = (categoryId) => {
    const cat = TASK_CATEGORIES.find((c) => c.id === categoryId);
    return cat?.label || categoryId;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <ClipboardList className="w-6 h-6 text-blue-600" />
            Task Templates
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Manage task templates for WMS operations
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={fetchTemplates}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={() => setShowAddForm(true)} data-testid="add-template-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Task Template
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <ClipboardList className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{templates.custom_templates?.length || 0}</p>
              <p className="text-sm text-slate-500">Custom Templates</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
              <ClipboardList className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{templates.system_defaults?.length || 0}</p>
              <p className="text-sm text-slate-500">System Defaults</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
              <ClipboardList className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{templates.total || 0}</p>
              <p className="text-sm text-slate-500">Total Available</p>
            </div>
          </div>
        </div>
      </div>

      {/* Custom Templates Section */}
      <div className="bg-white rounded-xl border">
        <div className="p-4 border-b">
          <h3 className="font-semibold flex items-center gap-2">
            <Plus className="w-4 h-4 text-blue-600" />
            Custom Task Templates
          </h3>
          <p className="text-sm text-slate-500 mt-1">
            These tasks appear as quick-add options for WMS orders
          </p>
        </div>
        <div className="p-4">
          {templates.custom_templates?.length > 0 ? (
            <div className="grid gap-3">
              {templates.custom_templates.map((template) => (
                <div
                  key={template.id}
                  className="flex items-start justify-between p-4 bg-slate-50 rounded-lg border"
                >
                  <div className="flex items-start gap-3">
                    <ClipboardList className="w-5 h-5 text-slate-400 mt-0.5" />
                    <div>
                      <p className="font-medium">{template.label}</p>
                      {template.description && (
                        <p className="text-sm text-slate-500">{template.description}</p>
                      )}
                      <div className="flex items-center gap-2 mt-2">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${getCategoryStyle(template.category)}`}>
                          {getCategoryLabel(template.category)}
                        </span>
                        <span className="text-xs text-slate-400">by {template.created_by}</span>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDeleteTemplate(template.id)}
                    className="p-2 text-red-500 hover:bg-red-50 rounded"
                    title="Delete template"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <ClipboardList className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p>No custom templates yet</p>
              <Button size="sm" className="mt-3" onClick={() => setShowAddForm(true)}>
                <Plus className="w-4 h-4 mr-1" />
                Create First Template
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* System Default Templates */}
      <div className="bg-white rounded-xl border">
        <div className="p-4 border-b">
          <h3 className="font-semibold flex items-center gap-2">
            <ClipboardList className="w-4 h-4 text-green-600" />
            System Default Tasks
          </h3>
          <p className="text-sm text-slate-500 mt-1">
            Built-in task templates (cannot be deleted)
          </p>
        </div>
        <div className="p-4">
          <div className="flex flex-wrap gap-2">
            {templates.system_defaults?.map((template) => (
              <div
                key={template.id}
                className="inline-flex items-center gap-2 px-3 py-2 bg-slate-50 border rounded-lg text-sm hover:bg-slate-100 cursor-default"
                title={template.description}
              >
                <span className="text-green-600">+</span>
                <span>{template.label}</span>
                <span className={`px-1.5 py-0.5 rounded text-xs ${getCategoryStyle(template.category)}`}>
                  {getCategoryLabel(template.category)}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Add Template Modal */}
      <AnimatePresence>
        {showAddForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowAddForm(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-4 border-b flex items-center justify-between">
                <h3 className="font-semibold">Add Task Template</h3>
                <button onClick={() => setShowAddForm(false)}>
                  <X className="w-5 h-5 text-slate-400" />
                </button>
              </div>
              <div className="p-4 space-y-4">
                <div>
                  <label className="text-sm font-medium mb-1 block">Task Name *</label>
                  <Input
                    value={newTemplate.label}
                    onChange={(e) => setNewTemplate({ ...newTemplate, label: e.target.value })}
                    placeholder="e.g., Check temperature logs"
                    data-testid="template-label"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Description</label>
                  <Textarea
                    value={newTemplate.description}
                    onChange={(e) => setNewTemplate({ ...newTemplate, description: e.target.value })}
                    placeholder="Brief description of this task..."
                    rows={2}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Category</label>
                  <select
                    value={newTemplate.category}
                    onChange={(e) => setNewTemplate({ ...newTemplate, category: e.target.value })}
                    className="w-full h-10 border rounded-md px-3"
                  >
                    {TASK_CATEGORIES.map((cat) => (
                      <option key={cat.id} value={cat.id}>{cat.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="p-4 border-t flex gap-3">
                <Button variant="outline" onClick={() => setShowAddForm(false)} className="flex-1">
                  Cancel
                </Button>
                <Button onClick={handleAddTemplate} disabled={saving} className="flex-1" data-testid="save-template-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                  Add Template
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ==========================================
// DASHBOARD TAB
// ==========================================

function DashboardTab({ dashboard, onRefresh }) {
  if (!dashboard) return null;

  const stats = [
    { label: "Today's Arrivals", value: dashboard.todays_arrivals, icon: Truck, color: "blue" },
    { label: "Pending Check-ins", value: dashboard.pending_checkins, icon: Clock, color: "orange" },
    { label: "Active Operations", value: dashboard.active_operations, icon: Package, color: "green" },
    { label: "Available Ramps", value: `${dashboard.available_ramps}/${dashboard.ramps?.length || 5}`, icon: MapPin, color: "purple" },
    { label: "Today's Events", value: dashboard.todays_events, icon: ClipboardList, color: "slate" },
    { label: "Damage Reports", value: dashboard.damage_reports, icon: AlertTriangle, color: "red" },
  ];

  const colorMap = {
    blue: "bg-blue-50 text-blue-700 border-blue-200",
    orange: "bg-orange-50 text-orange-700 border-orange-200",
    green: "bg-green-50 text-green-700 border-green-200",
    purple: "bg-purple-50 text-purple-700 border-purple-200",
    slate: "bg-slate-50 text-slate-700 border-slate-200",
    red: "bg-red-50 text-red-700 border-red-200",
  };

  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {stats.map((stat, idx) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.05 }}
            className={`p-4 rounded-xl border ${colorMap[stat.color]}`}
          >
            <stat.icon className="w-5 h-5 mb-2 opacity-70" />
            <p className="text-2xl font-bold">{stat.value}</p>
            <p className="text-xs opacity-70">{stat.label}</p>
          </motion.div>
        ))}
      </div>

      {/* Ramps Status */}
      <div className="bg-white rounded-xl border p-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <MapPin className="w-5 h-5 text-blue-600" />
          Ramp Status
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {dashboard.ramps?.map((ramp) => (
            <div
              key={ramp.id}
              className={`p-4 rounded-lg border-2 ${
                ramp.status === "available"
                  ? "border-green-300 bg-green-50"
                  : ramp.status === "occupied"
                  ? "border-red-300 bg-red-50"
                  : "border-yellow-300 bg-yellow-50"
              }`}
            >
              <p className="font-medium text-sm">{ramp.name}</p>
              <p className="text-xs text-slate-500 capitalize">{ramp.type?.replace("_", " ")}</p>
              <span className={`inline-block mt-2 px-2 py-0.5 rounded text-xs font-medium ${
                ramp.status === "available"
                  ? "bg-green-200 text-green-800"
                  : ramp.status === "occupied"
                  ? "bg-red-200 text-red-800"
                  : "bg-yellow-200 text-yellow-800"
              }`}>
                {ramp.status}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <QuickActionCard
          icon={Plus}
          label="New Pre-Alert"
          description="Schedule truck arrival"
          color="blue"
        />
        <QuickActionCard
          icon={Truck}
          label="Truck Check-in"
          description="Register arrival"
          color="green"
        />
        <QuickActionCard
          icon={Camera}
          label="Driver App"
          description="Load/Unload/Damage"
          color="purple"
        />
        <QuickActionCard
          icon={Eye}
          label="Customer View"
          description="Track shipment"
          color="orange"
        />
      </div>
    </div>
  );
}

function QuickActionCard({ icon: Icon, label, description, color }) {
  const colors = {
    blue: "from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700",
    green: "from-green-500 to-green-600 hover:from-green-600 hover:to-green-700",
    purple: "from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700",
    orange: "from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700",
  };

  return (
    <motion.button
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      className={`p-6 rounded-xl bg-gradient-to-br ${colors[color]} text-white text-left shadow-lg`}
    >
      <Icon className="w-8 h-8 mb-3 opacity-90" />
      <p className="font-semibold">{label}</p>
      <p className="text-xs opacity-80">{description}</p>
    </motion.button>
  );
}

// ==========================================
// TASKS TAB
// ==========================================

function TasksTab() {
  const [tasks, setTasks] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [templates, setTemplates] = useState([]);
  const [newTemplateLabel, setNewTemplateLabel] = useState("");

  useEffect(() => {
    fetchTasks();
    fetchSummary();
    fetchTemplates();
  }, [filter]);

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/wms/tasks/all?status=${filter}`);
      setTasks(response.data.tasks || []);
    } catch (error) {
      console.error("Failed to fetch tasks", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const response = await axios.get(`${API_URL}/wms/tasks/summary`);
      setSummary(response.data);
    } catch (error) {
      console.error("Failed to fetch summary", error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API_URL}/wms/tasks/templates`);
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error("Failed to fetch templates", error);
    }
  };

  const toggleTask = async (orderId, taskId) => {
    try {
      await axios.put(`${API_URL}/wms/orders/${orderId}/tasks/${taskId}/toggle-with-sync?completed_by=staff`);
      
      // Update local state
      setTasks(tasks.map(t => 
        t.id === taskId ? { ...t, completed: !t.completed } : t
      ));
      
      // Refresh summary
      fetchSummary();
      
      toast.success("Task updated");
    } catch (error) {
      toast.error("Failed to update task");
    }
  };

  const createTemplate = async () => {
    if (!newTemplateLabel.trim()) return;
    
    try {
      await axios.post(`${API_URL}/wms/tasks/templates`, {
        label: newTemplateLabel.trim(),
        direction: "inbound",
      });
      setNewTemplateLabel("");
      fetchTemplates();
      toast.success("Template created");
    } catch (error) {
      toast.error("Failed to create template");
    }
  };

  const deleteTemplate = async (templateId) => {
    try {
      await axios.delete(`${API_URL}/wms/tasks/templates/${templateId}`);
      fetchTemplates();
      toast.success("Template deleted");
    } catch (error) {
      toast.error("Failed to delete template");
    }
  };

  return (
    <div className="space-y-6">
      {/* Header & Summary */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <CheckCircle className="w-6 h-6 text-blue-600" />
            Task Manager
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Track and manage tasks across all warehouse orders
          </p>
        </div>
        
        {/* Summary Stats */}
        {summary && (
          <div className="flex gap-4">
            <div className="bg-white rounded-lg border px-4 py-2 text-center">
              <p className="text-2xl font-bold text-orange-600">{summary.pending_tasks}</p>
              <p className="text-xs text-slate-500">Pending</p>
            </div>
            <div className="bg-white rounded-lg border px-4 py-2 text-center">
              <p className="text-2xl font-bold text-green-600">{summary.completed_tasks}</p>
              <p className="text-xs text-slate-500">Completed</p>
            </div>
            <div className="bg-white rounded-lg border px-4 py-2 text-center">
              <p className="text-2xl font-bold text-blue-600">{summary.completion_rate}%</p>
              <p className="text-xs text-slate-500">Rate</p>
            </div>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex gap-2">
        {[
          { value: "pending", label: "Pending", color: "orange" },
          { value: "completed", label: "Completed", color: "green" },
          { value: "all", label: "All Tasks", color: "blue" },
        ].map((opt) => (
          <button
            key={opt.value}
            onClick={() => setFilter(opt.value)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === opt.value
                ? `bg-${opt.color}-600 text-white`
                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
            style={{
              backgroundColor: filter === opt.value 
                ? opt.color === "orange" ? "#ea580c" 
                : opt.color === "green" ? "#16a34a" 
                : "#2563eb" 
                : undefined
            }}
          >
            {opt.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Task List */}
        <div className="lg:col-span-2">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : tasks.length > 0 ? (
            <div className="bg-white rounded-xl border divide-y">
              {tasks.map((task) => (
                <div
                  key={`${task.order_id}-${task.id}`}
                  className={`p-4 hover:bg-slate-50 transition-colors ${
                    task.completed ? "bg-green-50/50" : ""
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {/* Checkbox */}
                    <button
                      onClick={() => toggleTask(task.order_id, task.id)}
                      className={`w-6 h-6 mt-0.5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                        task.completed
                          ? "bg-green-500 border-green-500 text-white"
                          : "border-slate-300 hover:border-blue-400"
                      }`}
                    >
                      {task.completed && <CheckCircle className="w-4 h-4" />}
                    </button>
                    
                    {/* Task Content */}
                    <div className="flex-1 min-w-0">
                      <p className={`font-medium ${task.completed ? "line-through text-slate-500" : ""}`}>
                        {task.label}
                      </p>
                      {task.description && (
                        <p className="text-sm text-slate-500">{task.description}</p>
                      )}
                      <div className="flex flex-wrap gap-2 mt-2">
                        <span className="text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-700">
                          {task.order_number}
                        </span>
                        <span className="text-xs text-slate-500">
                          {task.customer_name}
                        </span>
                        {task.assigned_to && (
                          <span className="text-xs px-2 py-0.5 rounded bg-purple-100 text-purple-700">
                            {task.assigned_to}
                          </span>
                        )}
                        {task.due_date && (
                          <span className="text-xs px-2 py-0.5 rounded bg-orange-100 text-orange-700">
                            Due: {task.due_date}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* Order Status Badge */}
                    <span className={`text-xs px-2 py-1 rounded-full flex-shrink-0 ${
                      task.order_status === "completed" ? "bg-green-100 text-green-700" :
                      task.order_status === "in_progress" ? "bg-yellow-100 text-yellow-700" :
                      "bg-slate-100 text-slate-600"
                    }`}>
                      {task.order_status?.replace("_", " ")}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-xl border p-12 text-center">
              <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-400" />
              <p className="text-slate-500">
                {filter === "pending" ? "No pending tasks!" : "No tasks found"}
              </p>
            </div>
          )}
        </div>

        {/* Templates Sidebar */}
        <div className="space-y-4">
          <div className="bg-white rounded-xl border p-4">
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <FileText className="w-5 h-5 text-slate-500" />
              Task Templates
            </h3>
            
            {/* Add New Template */}
            <div className="flex gap-2 mb-4">
              <Input
                value={newTemplateLabel}
                onChange={(e) => setNewTemplateLabel(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && createTemplate()}
                placeholder="New template..."
                className="flex-1"
              />
              <Button size="sm" onClick={createTemplate} disabled={!newTemplateLabel.trim()}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            
            {/* Template List */}
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {templates.map((template) => (
                <div
                  key={template.id}
                  className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm"
                >
                  <span>{template.label}</span>
                  <button
                    onClick={() => deleteTemplate(template.id)}
                    className="text-slate-400 hover:text-red-500 p-1"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
              {templates.length === 0 && (
                <p className="text-sm text-slate-400 text-center py-2">No custom templates</p>
              )}
            </div>
          </div>

          {/* Pending by Status */}
          {summary?.pending_by_order_status && Object.keys(summary.pending_by_order_status).length > 0 && (
            <div className="bg-white rounded-xl border p-4">
              <h3 className="font-semibold mb-3">Pending by Order Status</h3>
              <div className="space-y-2">
                {Object.entries(summary.pending_by_order_status).map(([status, count]) => (
                  <div key={status} className="flex items-center justify-between text-sm">
                    <span className="capitalize">{status.replace("_", " ")}</span>
                    <span className="font-medium text-orange-600">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ==========================================
// BL PHOTOS TAB - Photo Capture per BL
// ==========================================

function BLPhotosTab({ staffEmail }) {
  const [blNumber, setBlNumber] = useState("");
  const [orderId, setOrderId] = useState("");
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [photoType, setPhotoType] = useState("general");
  const [notes, setNotes] = useState("");
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [viewMode, setViewMode] = useState("capture"); // capture, browse, order
  const fileInputRef = useRef(null);

  // Fetch recent orders with BL numbers
  useEffect(() => {
    fetchRecentOrders();
  }, []);

  const fetchRecentOrders = async () => {
    try {
      const response = await axios.get(`${API_URL}/wms/orders?limit=20`);
      const ordersWithBLs = (response.data.orders || []).filter(o => o.bl_numbers?.length > 0);
      setOrders(ordersWithBLs);
    } catch (error) {
      console.error("Failed to fetch orders", error);
    }
  };

  const fetchPhotosForBL = async (bl) => {
    if (!bl) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/wms/bl-photos/${encodeURIComponent(bl)}`);
      setPhotos(response.data.photos || []);
    } catch (error) {
      console.error("Failed to fetch BL photos", error);
      toast.error("Failed to load photos");
    } finally {
      setLoading(false);
    }
  };

  const fetchPhotosForOrder = async (order) => {
    if (!order?.id) return;
    setLoading(true);
    setSelectedOrder(order);
    try {
      const response = await axios.get(`${API_URL}/wms/bl-photos/order/${order.id}`);
      setPhotos(response.data.photos || []);
    } catch (error) {
      console.error("Failed to fetch order photos", error);
      toast.error("Failed to load photos");
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoCapture = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!blNumber.trim()) {
      toast.error("Please enter a BL number first");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("bl_number", blNumber.trim());
    formData.append("order_id", orderId || "");
    formData.append("photo_type", photoType);
    formData.append("notes", notes);
    formData.append("operator_id", staffEmail || "unknown");

    try {
      const response = await axios.post(`${API_URL}/wms/bl-photos/upload`, formData);
      toast.success(`Photo uploaded for BL: ${blNumber}`);
      
      // Add to local photos array
      setPhotos(prev => [{
        id: response.data.photo_id,
        url: response.data.url,
        bl_number: blNumber,
        photo_type: photoType,
        notes: notes,
        uploaded_at: new Date().toISOString()
      }, ...prev]);
      
      // Reset notes after upload
      setNotes("");
    } catch (error) {
      console.error("Photo upload failed", error);
      toast.error("Failed to upload photo");
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!confirm("Delete this photo?")) return;
    
    try {
      await axios.delete(`${API_URL}/wms/bl-photos/${photoId}`);
      setPhotos(prev => prev.filter(p => p.id !== photoId));
      toast.success("Photo deleted");
    } catch (error) {
      toast.error("Failed to delete photo");
    }
  };

  const photoTypes = [
    { value: "general", label: "General", color: "bg-slate-100 text-slate-700" },
    { value: "damage", label: "Damage", color: "bg-red-100 text-red-700" },
    { value: "inspection", label: "Inspection", color: "bg-blue-100 text-blue-700" },
    { value: "delivery", label: "Delivery", color: "bg-green-100 text-green-700" },
    { value: "packing", label: "Packing", color: "bg-purple-100 text-purple-700" },
  ];

  return (
    <div className="space-y-6">
      {/* View Mode Toggle */}
      <div className="flex gap-2">
        <Button
          variant={viewMode === "capture" ? "default" : "outline"}
          size="sm"
          onClick={() => setViewMode("capture")}
        >
          <Camera className="w-4 h-4 mr-2" />
          Capture
        </Button>
        <Button
          variant={viewMode === "browse" ? "default" : "outline"}
          size="sm"
          onClick={() => setViewMode("browse")}
        >
          <Image className="w-4 h-4 mr-2" />
          Browse by BL
        </Button>
        <Button
          variant={viewMode === "order" ? "default" : "outline"}
          size="sm"
          onClick={() => setViewMode("order")}
        >
          <Package className="w-4 h-4 mr-2" />
          By Order
        </Button>
      </div>

      {/* Capture Mode */}
      {viewMode === "capture" && (
        <div className="bg-white rounded-xl p-6 shadow-sm border space-y-4">
          <h3 className="font-semibold text-lg flex items-center gap-2">
            <Camera className="w-5 h-5 text-blue-600" />
            Upload Photo for BL
          </h3>

          {/* BL Number Input */}
          <div>
            <label className="text-sm font-medium text-slate-600 mb-1 block">BL Number *</label>
            <Input
              value={blNumber}
              onChange={(e) => setBlNumber(e.target.value.toUpperCase())}
              placeholder="Enter BL number (e.g., MSCUTEST123)"
              className="font-mono"
              data-testid="bl-photo-bl-number"
            />
          </div>

          {/* Order ID (optional) */}
          <div>
            <label className="text-sm font-medium text-slate-600 mb-1 block">Order ID (optional)</label>
            <select
              value={orderId}
              onChange={(e) => setOrderId(e.target.value)}
              className="w-full border rounded-lg p-2"
            >
              <option value="">-- Select Order --</option>
              {orders.map(order => (
                <option key={order.id} value={order.id}>
                  {order.order_number} - {order.customer_name || order.carrier_name}
                </option>
              ))}
            </select>
          </div>

          {/* Photo Type */}
          <div>
            <label className="text-sm font-medium text-slate-600 mb-2 block">Photo Type</label>
            <div className="flex flex-wrap gap-2">
              {photoTypes.map(type => (
                <button
                  key={type.value}
                  onClick={() => setPhotoType(type.value)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                    photoType === type.value 
                      ? `${type.color} ring-2 ring-offset-1 ring-blue-500` 
                      : "bg-slate-50 text-slate-500 hover:bg-slate-100"
                  }`}
                  data-testid={`photo-type-${type.value}`}
                >
                  {type.label}
                </button>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="text-sm font-medium text-slate-600 mb-1 block">Notes (optional)</label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add notes about this photo..."
              rows={2}
            />
          </div>

          {/* Camera Capture Button */}
          <div className="pt-4">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handlePhotoCapture}
              disabled={uploading || !blNumber.trim()}
              className="hidden"
              id="bl-photo-input"
            />
            <label
              htmlFor="bl-photo-input"
              className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-xl cursor-pointer transition-colors ${
                uploading || !blNumber.trim()
                  ? "border-slate-200 bg-slate-50 cursor-not-allowed"
                  : "border-blue-300 bg-blue-50 hover:bg-blue-100 hover:border-blue-400"
              }`}
            >
              {uploading ? (
                <Loader2 className="w-10 h-10 animate-spin text-blue-500" />
              ) : (
                <>
                  <Camera className="w-10 h-10 text-blue-500 mb-2" />
                  <span className="text-sm text-blue-600 font-medium">
                    {blNumber.trim() ? "Tap to capture photo" : "Enter BL number first"}
                  </span>
                </>
              )}
            </label>
          </div>

          {/* Recently uploaded photos for this BL */}
          {photos.length > 0 && blNumber && (
            <div className="pt-4 border-t">
              <h4 className="text-sm font-medium text-slate-600 mb-3">
                Recent photos for {blNumber} ({photos.filter(p => p.bl_number === blNumber).length})
              </h4>
              <div className="grid grid-cols-3 gap-2">
                {photos.filter(p => p.bl_number === blNumber).slice(0, 6).map(photo => (
                  <div key={photo.id} className="relative group">
                    <img
                      src={`${API_URL.replace('/api', '')}${photo.url}`}
                      alt="BL Photo"
                      className="w-full h-20 object-cover rounded-lg"
                    />
                    <span className={`absolute top-1 left-1 text-xs px-1.5 py-0.5 rounded ${
                      photoTypes.find(t => t.value === photo.photo_type)?.color || "bg-slate-100"
                    }`}>
                      {photo.photo_type}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Browse by BL Mode */}
      {viewMode === "browse" && (
        <div className="bg-white rounded-xl p-6 shadow-sm border space-y-4">
          <h3 className="font-semibold text-lg flex items-center gap-2">
            <Search className="w-5 h-5 text-blue-600" />
            Browse Photos by BL
          </h3>

          <div className="flex gap-2">
            <Input
              value={blNumber}
              onChange={(e) => setBlNumber(e.target.value.toUpperCase())}
              placeholder="Enter BL number to search..."
              className="font-mono"
              onKeyDown={(e) => e.key === "Enter" && fetchPhotosForBL(blNumber)}
            />
            <Button onClick={() => fetchPhotosForBL(blNumber)} disabled={loading || !blNumber.trim()}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            </Button>
          </div>

          {/* Photo Grid */}
          {photos.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {photos.map(photo => (
                <div key={photo.id} className="relative group bg-slate-50 rounded-lg overflow-hidden">
                  <img
                    src={`${API_URL.replace('/api', '')}${photo.url}`}
                    alt="BL Photo"
                    className="w-full h-32 object-cover"
                  />
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDeletePhoto(photo.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="p-2">
                    <span className={`text-xs px-1.5 py-0.5 rounded ${
                      photoTypes.find(t => t.value === photo.photo_type)?.color || "bg-slate-100"
                    }`}>
                      {photo.photo_type}
                    </span>
                    {photo.notes && (
                      <p className="text-xs text-slate-500 mt-1 truncate">{photo.notes}</p>
                    )}
                    <p className="text-xs text-slate-400 mt-1">
                      {new Date(photo.uploaded_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : !loading && blNumber && (
            <div className="text-center py-8 text-slate-500">
              <Image className="w-12 h-12 mx-auto mb-2 text-slate-300" />
              <p>No photos found for BL: {blNumber}</p>
            </div>
          )}
        </div>
      )}

      {/* Browse by Order Mode */}
      {viewMode === "order" && (
        <div className="bg-white rounded-xl p-6 shadow-sm border space-y-4">
          <h3 className="font-semibold text-lg flex items-center gap-2">
            <Package className="w-5 h-5 text-blue-600" />
            Browse Photos by Order
          </h3>

          {/* Orders with BLs */}
          <div className="grid gap-2 max-h-64 overflow-y-auto">
            {orders.length > 0 ? orders.map(order => (
              <button
                key={order.id}
                onClick={() => fetchPhotosForOrder(order)}
                className={`w-full text-left p-3 rounded-lg border transition-colors ${
                  selectedOrder?.id === order.id 
                    ? "border-blue-500 bg-blue-50" 
                    : "border-slate-200 hover:border-blue-300 hover:bg-slate-50"
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium">{order.order_number}</span>
                  <span className="text-xs text-slate-500">
                    {order.bl_numbers?.length || 0} BL(s)
                  </span>
                </div>
                <p className="text-sm text-slate-600">{order.customer_name || order.carrier_name}</p>
                <div className="flex flex-wrap gap-1 mt-1">
                  {order.bl_numbers?.slice(0, 3).map((bl, idx) => (
                    <span key={idx} className="text-xs bg-slate-100 px-1.5 py-0.5 rounded font-mono">
                      {bl}
                    </span>
                  ))}
                  {order.bl_numbers?.length > 3 && (
                    <span className="text-xs text-slate-400">+{order.bl_numbers.length - 3} more</span>
                  )}
                </div>
              </button>
            )) : (
              <p className="text-slate-500 text-center py-4">No orders with BL numbers found</p>
            )}
          </div>

          {/* Photos for selected order */}
          {selectedOrder && (
            <div className="pt-4 border-t">
              <h4 className="font-medium mb-3">
                Photos for Order: {selectedOrder.order_number}
                <span className="text-slate-500 font-normal ml-2">({photos.length} photos)</span>
              </h4>
              
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                </div>
              ) : photos.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {photos.map(photo => (
                    <div key={photo.id} className="relative group bg-slate-50 rounded-lg overflow-hidden">
                      <img
                        src={`${API_URL.replace('/api', '')}${photo.url}`}
                        alt="BL Photo"
                        className="w-full h-28 object-cover"
                      />
                      <div className="p-2">
                        <span className="text-xs font-mono bg-slate-200 px-1 rounded">
                          {photo.bl_number}
                        </span>
                        <span className={`ml-1 text-xs px-1.5 py-0.5 rounded ${
                          photoTypes.find(t => t.value === photo.photo_type)?.color || "bg-slate-100"
                        }`}>
                          {photo.photo_type}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-slate-500">
                  <Image className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                  <p>No photos for this order</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ==========================================
// WAREHOUSE MAP TAB - Visual Layout & BL Lookup
// ==========================================

function WarehouseMapTab({ staffEmail }) {
  const [zones, setZones] = useState([]);
  const [selectedZone, setSelectedZone] = useState(null);
  const [zoneCargo, setZoneCargo] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchBL, setSearchBL] = useState("");
  const [searchResult, setSearchResult] = useState(null);
  const [searching, setSearching] = useState(false);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [moveData, setMoveData] = useState({ bl_number: "", new_location: "", notes: "" });
  const [moving, setMoving] = useState(false);
  const [showLegend, setShowLegend] = useState(false);

  useEffect(() => {
    fetchZones();
  }, []);

  const fetchZones = async () => {
    try {
      const response = await axios.get(`${API_URL}/wms/warehouse/zones`);
      setZones(response.data.zones || []);
    } catch (error) {
      console.error("Failed to fetch zones", error);
    }
  };

  const fetchZoneCargo = async (zoneId) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/wms/warehouse/zone/${zoneId}`);
      setZoneCargo(response.data.cargo || []);
      setSelectedZone(response.data.zone);
    } catch (error) {
      console.error("Failed to fetch zone cargo", error);
      toast.error("Failed to load zone cargo");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchBL.trim()) return;
    setSearching(true);
    setSearchResult(null);
    try {
      const response = await axios.get(`${API_URL}/wms/warehouse/search?bl_number=${encodeURIComponent(searchBL)}`);
      setSearchResult(response.data);
      if (response.data.found && response.data.location) {
        // Highlight the zone on the map
        setSelectedZone({ id: response.data.location });
      }
    } catch (error) {
      console.error("Search failed", error);
      toast.error("Search failed");
    } finally {
      setSearching(false);
    }
  };

  const handleMove = async () => {
    if (!moveData.bl_number || !moveData.new_location) {
      toast.error("Please fill in BL number and new location");
      return;
    }
    setMoving(true);
    try {
      const formData = new FormData();
      formData.append("bl_number", moveData.bl_number);
      formData.append("new_location", moveData.new_location);
      formData.append("notes", moveData.notes || "");
      formData.append("operator_id", staffEmail || "unknown");
      
      const response = await axios.post(`${API_URL}/wms/warehouse/move`, formData);
      toast.success(response.data.message);
      setShowMoveModal(false);
      setMoveData({ bl_number: "", new_location: "", notes: "" });
      fetchZones();
      if (selectedZone) {
        fetchZoneCargo(selectedZone.id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Move failed");
    } finally {
      setMoving(false);
    }
  };

  // Zone colors based on type
  const getZoneColor = (zone) => {
    if (selectedZone?.id === zone.id) return "fill-blue-400 stroke-blue-600";
    if (zone.type === "storage") {
      const occupancy = zone.current_count / zone.capacity;
      if (occupancy > 0.8) return "fill-red-200 stroke-red-400";
      if (occupancy > 0.5) return "fill-yellow-200 stroke-yellow-400";
      return "fill-green-200 stroke-green-400";
    }
    if (zone.type === "ramp") return "fill-purple-200 stroke-purple-400";
    if (zone.type === "workshop") return "fill-slate-200 stroke-slate-400";
    if (zone.type === "office") return "fill-blue-100 stroke-blue-300";
    return "fill-slate-100 stroke-slate-300";
  };

  const storageZones = zones.filter(z => z.type === "storage");
  const getZoneData = (id) => zones.find(z => z.id === id) || { id, current_count: 0, capacity: 0 };

  return (
    <div className="space-y-6">
      {/* Search Bar */}
      <div className="bg-white rounded-xl p-4 shadow-sm border">
        <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
          <Search className="w-5 h-5 text-blue-600" />
          Quick BL Lookup
        </h3>
        <div className="flex gap-2">
          <Input
            value={searchBL}
            onChange={(e) => setSearchBL(e.target.value.toUpperCase())}
            placeholder="Enter BL number to find location..."
            className="font-mono"
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            data-testid="warehouse-bl-search"
          />
          <Button onClick={handleSearch} disabled={searching || !searchBL.trim()}>
            {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          </Button>
          <Button variant="outline" onClick={() => setShowMoveModal(true)}>
            <ArrowRight className="w-4 h-4 mr-1" />
            Move Cargo
          </Button>
        </div>
        
        {/* Search Result */}
        {searchResult && (
          <div className={`mt-3 p-3 rounded-lg ${searchResult.found ? "bg-green-50 border border-green-200" : "bg-red-50 border border-red-200"}`}>
            {searchResult.found ? (
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-green-800">
                    BL {searchResult.bl_number} found in <span className="font-bold">{searchResult.location}</span>
                  </p>
                  <p className="text-sm text-green-600">
                    Zone: {searchResult.zone_info?.name} ({searchResult.zone_info?.type})
                  </p>
                </div>
                <Button size="sm" variant="outline" onClick={() => fetchZoneCargo(searchResult.location)}>
                  View Zone
                </Button>
              </div>
            ) : (
              <p className="text-red-700">{searchResult.message}</p>
            )}
          </div>
        )}
      </div>

      {/* Warehouse Visual Map */}
      <div className="bg-white rounded-xl p-4 shadow-sm border">
        <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
          <MapPin className="w-5 h-5 text-blue-600" />
          Warehouse Layout
        </h3>
        
        {/* SVG Warehouse Map */}
        <div className="relative bg-slate-50 rounded-lg p-4 overflow-auto">
          <svg viewBox="0 0 700 600" className="w-full max-w-4xl mx-auto" style={{ minWidth: "600px" }}>
            {/* Background */}
            <rect x="0" y="0" width="700" height="600" fill="#f8fafc" stroke="#e2e8f0" strokeWidth="2" rx="8" />
            
            {/* VHOD2 - Top entrance */}
            <text x="280" y="25" className="text-sm fill-slate-500 font-medium">VHOD2</text>
            <line x1="180" y1="35" x2="380" y2="35" stroke="#94a3b8" strokeWidth="2" strokeDasharray="8,4" />
            
            {/* Loading Ramp Triangle */}
            <polygon points="420,35 480,70 420,70" fill="white" stroke="#1e293b" strokeWidth="3" />
            <line x1="425" y1="45" x2="425" y2="65" stroke="#1e293b" strokeWidth="1.5" />
            <line x1="435" y1="40" x2="435" y2="68" stroke="#1e293b" strokeWidth="1.5" />
            <line x1="445" y1="42" x2="445" y2="68" stroke="#1e293b" strokeWidth="1.5" />
            <line x1="455" y1="48" x2="455" y2="68" stroke="#1e293b" strokeWidth="1.5" />
            <line x1="465" y1="54" x2="465" y2="68" stroke="#1e293b" strokeWidth="1.5" />
            
            {/* RAMPA - Loading ramp area */}
            <g className="cursor-pointer hover:opacity-80 transition-opacity" onClick={() => fetchZoneCargo("RAMPA")}>
              <rect x="500" y="35" width="180" height="70" fill="#e9d5ff" stroke="#a855f7" strokeWidth="2" rx="6" />
              <text x="590" y="65" textAnchor="middle" className="text-base font-bold fill-purple-700">RAMPA</text>
              <text x="590" y="85" textAnchor="middle" className="text-xs fill-purple-600">Gates 1 & 2</text>
            </g>
            
            {/* Forklift Office */}
            <rect x="30" y="35" width="100" height="60" fill="#dbeafe" stroke="#3b82f6" strokeWidth="2" rx="6" />
            <text x="80" y="60" textAnchor="middle" className="text-sm font-medium fill-blue-700">Forklift</text>
            <text x="80" y="78" textAnchor="middle" className="text-sm font-medium fill-blue-700">Office</text>
            
            {/* A Zone - Left column (A1, A2, A3) */}
            <g className="cursor-pointer hover:opacity-90 transition-opacity" onClick={() => fetchZoneCargo("A1")}>
              <rect x="30" y="120" width="80" height="100" className={`${getZoneColor(getZoneData("A1"))} stroke-2 transition-colors`} rx="8" />
              <text x="70" y="165" textAnchor="middle" className="text-xl font-bold fill-slate-700">A1</text>
              <text x="70" y="190" textAnchor="middle" className="text-sm fill-slate-500">{getZoneData("A1").current_count}</text>
            </g>
            <g className="cursor-pointer hover:opacity-90 transition-opacity" onClick={() => fetchZoneCargo("A2")}>
              <rect x="30" y="235" width="80" height="100" className={`${getZoneColor(getZoneData("A2"))} stroke-2 transition-colors`} rx="8" />
              <text x="70" y="280" textAnchor="middle" className="text-xl font-bold fill-slate-700">A2</text>
              <text x="70" y="305" textAnchor="middle" className="text-sm fill-slate-500">{getZoneData("A2").current_count}</text>
            </g>
            <g className="cursor-pointer hover:opacity-90 transition-opacity" onClick={() => fetchZoneCargo("A3")}>
              <rect x="30" y="350" width="80" height="90" className={`${getZoneColor(getZoneData("A3"))} stroke-2 transition-colors`} rx="8" />
              <text x="70" y="390" textAnchor="middle" className="text-xl font-bold fill-slate-700">A3</text>
              <text x="70" y="415" textAnchor="middle" className="text-sm fill-slate-500">{getZoneData("A3").current_count}</text>
            </g>
            
            {/* A Zone - Middle (A4, A5) */}
            <g className="cursor-pointer hover:opacity-90 transition-opacity" onClick={() => fetchZoneCargo("A4")}>
              <rect x="200" y="120" width="100" height="140" className={`${getZoneColor(getZoneData("A4"))} stroke-2 transition-colors`} rx="8" />
              <text x="250" y="185" textAnchor="middle" className="text-xl font-bold fill-slate-700">A4</text>
              <text x="250" y="215" textAnchor="middle" className="text-sm fill-slate-500">{getZoneData("A4").current_count}</text>
            </g>
            <g className="cursor-pointer hover:opacity-90 transition-opacity" onClick={() => fetchZoneCargo("A5")}>
              <rect x="320" y="120" width="110" height="140" className={`${getZoneColor(getZoneData("A5"))} stroke-2 transition-colors`} rx="8" />
              <text x="375" y="185" textAnchor="middle" className="text-xl font-bold fill-slate-700">A5</text>
              <text x="375" y="215" textAnchor="middle" className="text-sm fill-slate-500">{getZoneData("A5").current_count}</text>
            </g>
            
            {/* C Zone - Middle */}
            <g className="cursor-pointer hover:opacity-90 transition-opacity" onClick={() => fetchZoneCargo("C1")}>
              <rect x="200" y="290" width="100" height="120" className={`${getZoneColor(getZoneData("C1"))} stroke-2 transition-colors`} rx="8" />
              <text x="250" y="345" textAnchor="middle" className="text-xl font-bold fill-slate-700">C1</text>
              <text x="250" y="375" textAnchor="middle" className="text-sm fill-slate-500">{getZoneData("C1").current_count}</text>
            </g>
            <g className="cursor-pointer hover:opacity-90 transition-opacity" onClick={() => fetchZoneCargo("C3")}>
              <rect x="140" y="450" width="100" height="110" className={`${getZoneColor(getZoneData("C3"))} stroke-2 transition-colors`} rx="8" />
              <text x="190" y="500" textAnchor="middle" className="text-xl font-bold fill-slate-700">C3</text>
              <text x="190" y="530" textAnchor="middle" className="text-sm fill-slate-500">{getZoneData("C3").current_count}</text>
            </g>
            <g className="cursor-pointer hover:opacity-90 transition-opacity" onClick={() => fetchZoneCargo("C2")}>
              <rect x="260" y="450" width="120" height="110" className={`${getZoneColor(getZoneData("C2"))} stroke-2 transition-colors`} rx="8" />
              <text x="320" y="500" textAnchor="middle" className="text-xl font-bold fill-slate-700">C2</text>
              <text x="320" y="530" textAnchor="middle" className="text-sm fill-slate-500">{getZoneData("C2").current_count}</text>
            </g>
            
            {/* B Zone - Middle right (B6, B5) */}
            <g className="cursor-pointer hover:opacity-90 transition-opacity" onClick={() => fetchZoneCargo("B6")}>
              <rect x="460" y="280" width="90" height="100" className={`${getZoneColor(getZoneData("B6"))} stroke-2 transition-colors`} rx="8" />
              <text x="505" y="325" textAnchor="middle" className="text-xl font-bold fill-slate-700">B6</text>
              <text x="505" y="355" textAnchor="middle" className="text-sm fill-slate-500">{getZoneData("B6").current_count}</text>
            </g>
            <g className="cursor-pointer hover:opacity-90 transition-opacity" onClick={() => fetchZoneCargo("B5")}>
              <rect x="460" y="400" width="90" height="100" className={`${getZoneColor(getZoneData("B5"))} stroke-2 transition-colors`} rx="8" />
              <text x="505" y="445" textAnchor="middle" className="text-xl font-bold fill-slate-700">B5</text>
              <text x="505" y="475" textAnchor="middle" className="text-sm fill-slate-500">{getZoneData("B5").current_count}</text>
            </g>
            
            {/* B Zone - Right column (B1, B2, B3, B4) */}
            <g className="cursor-pointer hover:opacity-90 transition-opacity" onClick={() => fetchZoneCargo("B1")}>
              <rect x="590" y="140" width="80" height="90" className={`${getZoneColor(getZoneData("B1"))} stroke-2 transition-colors`} rx="8" />
              <text x="630" y="180" textAnchor="middle" className="text-xl font-bold fill-slate-700">B1</text>
              <text x="630" y="205" textAnchor="middle" className="text-sm fill-slate-500">{getZoneData("B1").current_count}</text>
            </g>
            <g className="cursor-pointer hover:opacity-90 transition-opacity" onClick={() => fetchZoneCargo("B2")}>
              <rect x="590" y="250" width="80" height="90" className={`${getZoneColor(getZoneData("B2"))} stroke-2 transition-colors`} rx="8" />
              <text x="630" y="290" textAnchor="middle" className="text-xl font-bold fill-slate-700">B2</text>
              <text x="630" y="315" textAnchor="middle" className="text-sm fill-slate-500">{getZoneData("B2").current_count}</text>
            </g>
            <g className="cursor-pointer hover:opacity-90 transition-opacity" onClick={() => fetchZoneCargo("B3")}>
              <rect x="590" y="360" width="80" height="80" className={`${getZoneColor(getZoneData("B3"))} stroke-2 transition-colors`} rx="8" />
              <text x="630" y="395" textAnchor="middle" className="text-xl font-bold fill-slate-700">B3</text>
              <text x="630" y="420" textAnchor="middle" className="text-sm fill-slate-500">{getZoneData("B3").current_count}</text>
            </g>
            <g className="cursor-pointer hover:opacity-90 transition-opacity" onClick={() => fetchZoneCargo("B4")}>
              <rect x="590" y="460" width="80" height="100" className={`${getZoneColor(getZoneData("B4"))} stroke-2 transition-colors`} rx="8" />
              <text x="630" y="505" textAnchor="middle" className="text-xl font-bold fill-slate-700">B4</text>
              <text x="630" y="535" textAnchor="middle" className="text-sm fill-slate-500">{getZoneData("B4").current_count}</text>
            </g>
            
            {/* Bottom Left Area - DELAVNICA / VHOD / PISARNA */}
            {/* DELAVNICA - Workshop (top) */}
            <rect x="30" y="450" width="90" height="45" fill="#f1f5f9" stroke="#64748b" strokeWidth="2" rx="6" />
            <text x="75" y="478" textAnchor="middle" className="text-xs font-medium fill-slate-600">DELAVNICA</text>
            
            {/* VHOD - Entrance (middle - doorway style) */}
            <g>
              <rect x="30" y="505" width="90" height="35" fill="#fef3c7" stroke="#f59e0b" strokeWidth="2" rx="0" />
              <line x1="75" y1="505" x2="75" y2="540" stroke="#f59e0b" strokeWidth="2" strokeDasharray="4,3" />
              <text x="75" y="527" textAnchor="middle" className="text-sm font-bold fill-amber-700">VHOD</text>
              {/* Door arrows */}
              <path d="M 45 522 L 55 517 L 55 527 Z" fill="#f59e0b" />
              <path d="M 105 522 L 95 517 L 95 527 Z" fill="#f59e0b" />
            </g>
            
            {/* PISARNA - Office (bottom) */}
            <rect x="30" y="550" width="90" height="40" fill="#dbeafe" stroke="#3b82f6" strokeWidth="2" rx="6" />
            <text x="75" y="575" textAnchor="middle" className="text-sm font-medium fill-blue-700">PISARNA</text>
            
            {/* Legend - Hidden by default, show on hover */}
          </svg>
          
          {/* Floating Legend Button & Tooltip */}
          <div 
            className="absolute bottom-4 right-4"
            onMouseEnter={() => setShowLegend(true)}
            onMouseLeave={() => setShowLegend(false)}
          >
            <button className="bg-white border-2 border-slate-200 rounded-full p-2 shadow-md hover:shadow-lg transition-shadow">
              <Info className="w-5 h-5 text-slate-500" />
            </button>
            
            {/* Legend Tooltip */}
            {showLegend && (
              <div className="absolute bottom-12 right-0 bg-white border border-slate-200 rounded-lg shadow-xl p-4 w-48 z-10">
                <p className="font-semibold text-slate-700 mb-3 text-sm">Zone Occupancy</p>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-4 rounded bg-green-200 border-2 border-green-500"></div>
                    <span className="text-xs text-slate-600">&lt;50% full</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-4 rounded bg-yellow-200 border-2 border-yellow-500"></div>
                    <span className="text-xs text-slate-600">50-80% full</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-4 rounded bg-red-200 border-2 border-red-500"></div>
                    <span className="text-xs text-slate-600">&gt;80% full</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-4 rounded bg-blue-300 border-2 border-blue-500"></div>
                    <span className="text-xs text-slate-600">Selected</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* Zone Capacity Summary */}
        <div className="mt-4 grid grid-cols-3 md:grid-cols-6 gap-2">
          {storageZones.map(zone => (
            <button
              key={zone.id}
              onClick={() => fetchZoneCargo(zone.id)}
              className={`p-2 rounded-lg border text-center transition-colors ${
                selectedZone?.id === zone.id ? "bg-blue-100 border-blue-400" : "bg-slate-50 border-slate-200 hover:bg-slate-100"
              }`}
            >
              <span className="font-bold text-sm">{zone.id}</span>
              <div className="text-xs text-slate-500">
                {zone.current_count}/{zone.capacity}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Selected Zone Cargo List */}
      {selectedZone && (
        <div className="bg-white rounded-xl p-4 shadow-sm border">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-lg flex items-center gap-2">
              <Package className="w-5 h-5 text-blue-600" />
              Zone {selectedZone.id} - {selectedZone.name}
            </h3>
            <Button variant="outline" size="sm" onClick={() => setSelectedZone(null)}>
              <X className="w-4 h-4" />
            </Button>
          </div>
          
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
            </div>
          ) : zoneCargo.length > 0 ? (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {zoneCargo.map(cargo => (
                <div key={cargo.id} className="p-3 bg-slate-50 rounded-lg border flex items-center justify-between">
                  <div>
                    <span className="font-mono font-bold">{cargo.bl_number}</span>
                    {cargo.description && (
                      <p className="text-sm text-slate-500">{cargo.description}</p>
                    )}
                    <p className="text-xs text-slate-400">
                      Stored: {new Date(cargo.stored_at).toLocaleDateString()}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setMoveData({ bl_number: cargo.bl_number, new_location: "", notes: "" });
                      setShowMoveModal(true);
                    }}
                  >
                    <ArrowRight className="w-3 h-3 mr-1" />
                    Move
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <Package className="w-10 h-10 mx-auto mb-2 text-slate-300" />
              <p>No cargo in this zone</p>
            </div>
          )}
        </div>
      )}

      {/* Move Cargo Modal */}
      {showMoveModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md m-4 p-6">
            <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <ArrowRight className="w-5 h-5 text-blue-600" />
              Move Cargo
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-600 mb-1 block">BL Number</label>
                <Input
                  value={moveData.bl_number}
                  onChange={(e) => setMoveData({ ...moveData, bl_number: e.target.value.toUpperCase() })}
                  placeholder="Enter BL number"
                  className="font-mono"
                />
              </div>
              
              <div>
                <label className="text-sm font-medium text-slate-600 mb-1 block">New Location</label>
                <select
                  value={moveData.new_location}
                  onChange={(e) => setMoveData({ ...moveData, new_location: e.target.value })}
                  className="w-full border rounded-lg p-2"
                >
                  <option value="">-- Select Zone --</option>
                  {storageZones.map(zone => (
                    <option key={zone.id} value={zone.id}>
                      {zone.id} ({zone.current_count}/{zone.capacity})
                    </option>
                  ))}
                  <option value="RAMPA">RAMPA (Loading)</option>
                </select>
              </div>
              
              <div>
                <label className="text-sm font-medium text-slate-600 mb-1 block">Notes (optional)</label>
                <Textarea
                  value={moveData.notes}
                  onChange={(e) => setMoveData({ ...moveData, notes: e.target.value })}
                  placeholder="Add notes about this move..."
                  rows={2}
                />
              </div>
            </div>
            
            <div className="flex justify-end gap-3 mt-6">
              <Button variant="outline" onClick={() => setShowMoveModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleMove} disabled={moving || !moveData.bl_number || !moveData.new_location}>
                {moving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <ArrowRight className="w-4 h-4 mr-1" />}
                Move Cargo
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ==========================================
// PERFORMANCE TAB
// ==========================================

function PerformanceTab() {
  const [performance, setPerformance] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPerformance();
  }, []);

  const fetchPerformance = async () => {
    try {
      const response = await axios.get(`${API_URL}/wms/performance`);
      setPerformance(response.data);
    } catch (error) {
      console.error("Failed to fetch performance data", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!performance) {
    return (
      <div className="text-center py-20 text-slate-500">
        Failed to load performance data
      </div>
    );
  }

  const { weekly_transloads, customer_totals, summary } = performance;

  // Calculate max value for chart scaling
  const maxValue = Math.max(
    ...weekly_transloads.map((w) => Math.max(w.actual, w.target))
  );

  return (
    <div className="space-y-6">
      {/* KPI Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl border p-4"
        >
          <div className="flex items-center gap-2 text-slate-500 mb-1">
            <Package className="w-4 h-4" />
            <span className="text-xs font-medium">Total Transloads</span>
          </div>
          <p className="text-3xl font-bold text-slate-900">{summary.total_transloads}</p>
          <p className="text-xs text-slate-400 mt-1">YTD Target: {summary.ytd_target}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="bg-white rounded-xl border p-4"
        >
          <div className="flex items-center gap-2 text-slate-500 mb-1">
            <TrendingUp className="w-4 h-4" />
            <span className="text-xs font-medium">Avg Weekly</span>
          </div>
          <p className="text-3xl font-bold text-slate-900">{summary.avg_weekly.toFixed(1)}</p>
          <p className="text-xs text-slate-400 mt-1">Breakeven: 35/week</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl border p-4"
        >
          <div className="flex items-center gap-2 text-slate-500 mb-1">
            <Target className="w-4 h-4" />
            <span className="text-xs font-medium">Target Achievement</span>
          </div>
          <p className={`text-3xl font-bold ${summary.target_achievement >= 80 ? 'text-green-600' : summary.target_achievement >= 50 ? 'text-orange-600' : 'text-red-600'}`}>
            {summary.target_achievement}%
          </p>
          <p className="text-xs text-slate-400 mt-1">{summary.total_transloads}/{summary.ytd_target} transloads</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="bg-white rounded-xl border p-4"
        >
          <div className="flex items-center gap-2 text-slate-500 mb-1">
            <BarChart3 className="w-4 h-4" />
            <span className="text-xs font-medium">Best Week</span>
          </div>
          <p className="text-3xl font-bold text-green-600">{summary.best_week}</p>
          <p className="text-xs text-slate-400 mt-1">Worst: {summary.worst_week}</p>
        </motion.div>
      </div>

      {/* Weekly Performance Chart */}
      <div className="bg-white rounded-xl border p-6">
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-blue-600" />
          Weekly Transload Performance
        </h3>
        
        {/* Chart Legend */}
        <div className="flex gap-4 mb-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-blue-500 rounded" />
            <span>Actual</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-1 bg-red-500" />
            <span>Target (40)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-1 bg-orange-400 border-dashed border-orange-400" style={{ borderStyle: 'dashed', borderWidth: 2 }} />
            <span>Breakeven (35)</span>
          </div>
        </div>

        {/* Simple Bar Chart */}
        <div className="relative h-64">
          {/* Y-axis labels */}
          <div className="absolute left-0 top-0 bottom-8 w-8 flex flex-col justify-between text-xs text-slate-400">
            <span>{maxValue}</span>
            <span>{Math.round(maxValue / 2)}</span>
            <span>0</span>
          </div>

          {/* Chart area */}
          <div className="ml-10 h-full flex items-end gap-2 relative pb-8">
            {/* Target line */}
            <div 
              className="absolute left-0 right-0 border-t-2 border-red-500 z-10"
              style={{ bottom: `calc(${(40 / maxValue) * 100}% + 32px)` }}
            />
            {/* Breakeven line */}
            <div 
              className="absolute left-0 right-0 border-t-2 border-orange-400 border-dashed z-10"
              style={{ bottom: `calc(${(35 / maxValue) * 100}% + 32px)` }}
            />

            {weekly_transloads.map((week, idx) => (
              <div key={week.week} className="flex-1 flex flex-col items-center">
                <motion.div
                  initial={{ height: 0 }}
                  animate={{ height: `${(week.actual / maxValue) * 100}%` }}
                  transition={{ delay: idx * 0.05, duration: 0.5 }}
                  className={`w-full rounded-t-md ${
                    week.actual >= 35 ? 'bg-green-500' : week.actual >= 20 ? 'bg-orange-500' : 'bg-red-400'
                  }`}
                  style={{ minHeight: week.actual > 0 ? '4px' : '0' }}
                >
                  <div className="text-center text-white text-xs font-bold pt-1">
                    {week.actual > 0 ? week.actual : ''}
                  </div>
                </motion.div>
                <span className="text-xs text-slate-500 mt-2">W{week.week}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Top Customers */}
      <div className="bg-white rounded-xl border p-6">
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <Users className="w-5 h-5 text-blue-600" />
          Top Customers by Transloads
        </h3>

        <div className="space-y-3">
          {customer_totals.slice(0, 8).map((customer, idx) => {
            const percentage = (customer.total / summary.total_transloads) * 100;
            return (
              <motion.div
                key={customer.customer}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: idx * 0.05 }}
                className="flex items-center gap-4"
              >
                <span className="w-6 text-center text-sm font-bold text-slate-400">
                  {idx + 1}
                </span>
                <div className="flex-1">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm font-medium">{customer.customer}</span>
                    <span className="text-sm text-slate-500">{customer.total}</span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${percentage}%` }}
                      transition={{ delay: idx * 0.05 + 0.2, duration: 0.5 }}
                      className={`h-full rounded-full ${
                        idx === 0 ? 'bg-blue-600' : idx === 1 ? 'bg-blue-500' : idx === 2 ? 'bg-blue-400' : 'bg-blue-300'
                      }`}
                    />
                  </div>
                </div>
                <span className="text-xs text-slate-400 w-12 text-right">
                  {percentage.toFixed(1)}%
                </span>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ==========================================
// ORDERS TAB (PF-like with drag & drop)
// ==========================================

function OrdersTab() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewForm, setShowNewForm] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [filter, setFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    fetchOrders();
  }, [filter]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/wms/orders`;
      const params = [];
      if (filter !== "all") params.push(`status=${filter}`);
      if (searchQuery) params.push(`search=${encodeURIComponent(searchQuery)}`);
      if (params.length > 0) url += `?${params.join("&")}`;
      
      const response = await axios.get(url);
      setOrders(response.data.orders || []);
    } catch (error) {
      console.error("Failed to fetch orders", error);
      toast.error("Failed to load orders");
    } finally {
      setLoading(false);
    }
  };

  const statusColors = {
    new: "bg-blue-100 text-blue-700",
    confirmed: "bg-purple-100 text-purple-700",
    in_progress: "bg-yellow-100 text-yellow-700",
    pending_pickup: "bg-orange-100 text-orange-700",
    completed: "bg-green-100 text-green-700",
    cancelled: "bg-red-100 text-red-700",
  };

  const filterOptions = [
    { value: "all", label: "All Orders" },
    { value: "new", label: "New" },
    { value: "confirmed", label: "Confirmed" },
    { value: "in_progress", label: "In Progress" },
    { value: "pending_pickup", label: "Pending Pickup" },
    { value: "completed", label: "Completed" },
  ];

  // Email drag & drop state
  const [isDragging, setIsDragging] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [parsedResult, setParsedResult] = useState(null);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    const msgFile = files.find((f) => f.name.toLowerCase().endsWith(".msg") || f.name.toLowerCase().endsWith(".eml"));

    if (!msgFile) {
      toast.error("Please drop a .msg or .eml email file");
      return;
    }

    await parseEmailFile(msgFile);
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      await parseEmailFile(file);
    }
  };

  const parseEmailFile = async (file) => {
    setParsing(true);
    setParsedResult(null);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await axios.post(`${API_URL}/wms/email/parse`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setParsedResult(response.data);
      toast.success(`Email parsed using ${response.data.extraction_method === "ai" ? "AI" : "regex"}`);
    } catch (error) {
      toast.error("Failed to parse email");
    } finally {
      setParsing(false);
    }
  };

  const createOrderFromParsed = async () => {
    if (!parsedResult?.suggested_order) return;

    try {
      const orderData = parsedResult.suggested_order;
      
      const response = await axios.post(`${API_URL}/wms/orders`, {
        ...orderData,
        bl_numbers: orderData.bl_numbers || [],
        container_numbers: orderData.container_numbers || [],
        source_system: "email",
      });

      toast.success(`Order ${response.data.order?.order_number} created!`);
      setParsedResult(null);
      fetchOrders();
    } catch (error) {
      toast.error("Failed to create order");
    }
  };

  return (
    <div className="space-y-6">
      {/* Email Drop Zone - Quick Import */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-xl p-4 text-center transition-colors ${
          isDragging
            ? "border-blue-500 bg-blue-50"
            : "border-slate-200 hover:border-blue-300 bg-slate-50"
        }`}
      >
        {parsing ? (
          <div className="flex items-center justify-center gap-3 py-2">
            <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
            <span className="text-sm text-slate-600">Parsing email with AI...</span>
          </div>
        ) : parsedResult ? (
          <div className="text-left">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-purple-600" />
                <span className="text-sm font-medium text-purple-600">AI Extracted from: {parsedResult.email_preview?.subject?.slice(0, 50)}...</span>
              </div>
              <button onClick={() => setParsedResult(null)} className="text-slate-400 hover:text-slate-600">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-3">
              <div><span className="text-slate-500">Customer:</span> <span className="font-medium">{parsedResult.suggested_order?.customer_name || "-"}</span></div>
              <div><span className="text-slate-500">Container:</span> <span className="font-medium">{parsedResult.suggested_order?.container_numbers?.join(", ") || "-"}</span></div>
              <div><span className="text-slate-500">Weight:</span> <span className="font-medium">{parsedResult.suggested_order?.gross_weight_kg ? `${parsedResult.suggested_order.gross_weight_kg} kg` : "-"}</span></div>
              <div><span className="text-slate-500">Direction:</span> <span className="font-medium capitalize">{parsedResult.suggested_order?.direction || "-"}</span></div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={createOrderFromParsed}>
                <Plus className="w-3 h-3 mr-1" />
                Create Order
              </Button>
              <Button size="sm" variant="outline" onClick={() => { setParsedResult(null); setShowNewForm(true); }}>
                <Edit className="w-3 h-3 mr-1" />
                Edit First
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center gap-4 py-2">
            <div className="flex items-center gap-2 text-slate-500">
              <FileUp className="w-5 h-5" />
              <span className="text-sm">Drop .msg email to create order</span>
            </div>
            <span className="text-slate-300">or</span>
            <label className="cursor-pointer">
              <input type="file" accept=".msg,.eml" onChange={handleFileSelect} className="hidden" />
              <span className="text-sm text-blue-600 hover:text-blue-700 font-medium">Browse</span>
            </label>
          </div>
        )}
      </div>

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <ClipboardList className="w-6 h-6 text-blue-600" />
            WMS Orders
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Manage warehouse orders - drop emails or create manually
          </p>
        </div>
        <Button onClick={() => setShowNewForm(true)} data-testid="new-order-btn">
          <Plus className="w-4 h-4 mr-2" />
          New Order
        </Button>
      </div>

      {/* Filters & Search */}
      <div className="flex flex-wrap gap-4 items-center">
        <div className="flex gap-2">
          {filterOptions.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setFilter(opt.value)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                filter === opt.value
                  ? "bg-blue-600 text-white"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <div className="flex-1 max-w-xs">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && fetchOrders()}
              placeholder="Search orders, BL, container..."
              className="pl-10"
            />
          </div>
        </div>
      </div>

      {/* Order List */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      ) : orders.length > 0 ? (
        <div className="grid gap-4">
          {orders.map((order) => (
            <motion.div
              key={order.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl border p-4 hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => setSelectedOrder(order)}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold text-blue-600">{order.order_number}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[order.status] || statusColors.new}`}>
                      {order.status?.replace("_", " ")}
                    </span>
                    <span className={`px-2 py-0.5 rounded text-xs ${
                      order.direction === "inbound" ? "bg-green-50 text-green-700" : "bg-orange-50 text-orange-700"
                    }`}>
                      {order.direction}
                    </span>
                    {order.source_system && order.source_system !== "manual" && (
                      <span className="px-2 py-0.5 rounded text-xs bg-purple-50 text-purple-700">
                        via {order.source_system}
                      </span>
                    )}
                  </div>
                  <p className="font-medium mt-1">{order.customer_name}</p>
                  <div className="flex flex-wrap gap-4 mt-2 text-sm text-slate-500">
                    {order.reference_number && (
                      <span className="flex items-center gap-1">
                        <FileText className="w-3 h-3" />
                        {order.reference_number}
                      </span>
                    )}
                    {order.bl_numbers?.length > 0 && (
                      <span className="flex items-center gap-1">
                        <Ship className="w-3 h-3" />
                        {order.bl_numbers.join(", ")}
                      </span>
                    )}
                    {order.container_numbers?.length > 0 && (
                      <span className="flex items-center gap-1">
                        <Box className="w-3 h-3" />
                        {order.container_numbers.join(", ")}
                      </span>
                    )}
                    {order.packages && (
                      <span>{order.packages} pkgs</span>
                    )}
                    {order.gross_weight_kg && (
                      <span>{order.gross_weight_kg} kg</span>
                    )}
                  </div>
                  {/* Task Progress */}
                  {order.tasks?.length > 0 && (
                    <div className="mt-3 flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-green-500 rounded-full"
                          style={{
                            width: `${(order.tasks.filter((t) => t.completed).length / order.tasks.length) * 100}%`,
                          }}
                        />
                      </div>
                      <span className="text-xs text-slate-500">
                        {order.tasks.filter((t) => t.completed).length}/{order.tasks.length}
                      </span>
                    </div>
                  )}
                </div>
                <ChevronRight className="w-5 h-5 text-slate-400" />
              </div>
            </motion.div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl border p-12 text-center">
          <ClipboardList className="w-12 h-12 mx-auto mb-3 text-slate-300" />
          <p className="text-slate-500 mb-4">No orders found</p>
          <Button onClick={() => setShowNewForm(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Create First Order
          </Button>
        </div>
      )}

      {/* New Order Modal */}
      <AnimatePresence>
        {showNewForm && (
          <NewOrderModal onClose={() => setShowNewForm(false)} onSuccess={() => { setShowNewForm(false); fetchOrders(); }} />
        )}
      </AnimatePresence>

      {/* Order Detail Modal */}
      <AnimatePresence>
        {selectedOrder && (
          <OrderDetailModal
            order={selectedOrder}
            onClose={() => setSelectedOrder(null)}
            onUpdate={() => { fetchOrders(); }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// New Order Form Modal
function NewOrderModal({ onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    reference_number: "",
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    bl_numbers: "",
    container_numbers: "",
    cargo_description: "",
    packages: "",
    gross_weight_kg: "",
    volume_cbm: "",
    direction: "inbound",
    eta: "",
    special_instructions: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.customer_name || !formData.reference_number) {
      toast.error("Customer name and reference number are required");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/wms/orders`, {
        ...formData,
        bl_numbers: formData.bl_numbers ? formData.bl_numbers.split(",").map((s) => s.trim()) : [],
        container_numbers: formData.container_numbers ? formData.container_numbers.split(",").map((s) => s.trim()) : [],
        packages: formData.packages ? parseInt(formData.packages) : null,
        gross_weight_kg: formData.gross_weight_kg ? parseFloat(formData.gross_weight_kg) : null,
        volume_cbm: formData.volume_cbm ? parseFloat(formData.volume_cbm) : null,
        source_system: "manual",
      });
      toast.success("Order created!");
      onSuccess();
    } catch (error) {
      toast.error("Failed to create order");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        <div className="p-6 border-b flex items-center justify-between">
          <h3 className="text-lg font-semibold">New WMS Order</h3>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Customer Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Customer Name *</label>
              <Input
                value={formData.customer_name}
                onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                placeholder="SPEDMAN, SEG, etc."
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium">Reference Number *</label>
              <Input
                value={formData.reference_number}
                onChange={(e) => setFormData({ ...formData, reference_number: e.target.value })}
                placeholder="PO-12345"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Customer Email</label>
              <Input
                type="email"
                value={formData.customer_email}
                onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                placeholder="customer@email.com"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Direction</label>
              <select
                value={formData.direction}
                onChange={(e) => setFormData({ ...formData, direction: e.target.value })}
                className="w-full h-10 border rounded-md px-3"
              >
                <option value="inbound">Inbound (Receiving)</option>
                <option value="outbound">Outbound (Shipping)</option>
                <option value="crossdock">Cross-dock</option>
              </select>
            </div>
          </div>

          {/* Document Numbers */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">BL Numbers</label>
              <Input
                value={formData.bl_numbers}
                onChange={(e) => setFormData({ ...formData, bl_numbers: e.target.value })}
                placeholder="Comma separated"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Container Numbers</label>
              <Input
                value={formData.container_numbers}
                onChange={(e) => setFormData({ ...formData, container_numbers: e.target.value })}
                placeholder="MSCU1234567, CMAU..."
              />
            </div>
          </div>

          {/* Cargo Details */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium">Packages</label>
              <Input
                type="number"
                value={formData.packages}
                onChange={(e) => setFormData({ ...formData, packages: e.target.value })}
                placeholder="33"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Weight (kg)</label>
              <Input
                type="number"
                step="0.01"
                value={formData.gross_weight_kg}
                onChange={(e) => setFormData({ ...formData, gross_weight_kg: e.target.value })}
                placeholder="1500"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Volume (CBM)</label>
              <Input
                type="number"
                step="0.01"
                value={formData.volume_cbm}
                onChange={(e) => setFormData({ ...formData, volume_cbm: e.target.value })}
                placeholder="25.5"
              />
            </div>
          </div>

          <div>
            <label className="text-sm font-medium">Cargo Description</label>
            <Textarea
              value={formData.cargo_description}
              onChange={(e) => setFormData({ ...formData, cargo_description: e.target.value })}
              placeholder="Electronics, machinery parts, etc."
              rows={2}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">ETA</label>
              <Input
                type="datetime-local"
                value={formData.eta}
                onChange={(e) => setFormData({ ...formData, eta: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Special Instructions</label>
              <Input
                value={formData.special_instructions}
                onChange={(e) => setFormData({ ...formData, special_instructions: e.target.value })}
                placeholder="Fragile, temperature controlled..."
              />
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button type="submit" disabled={submitting} className="flex-1">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Create Order
            </Button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}

// Order Detail Modal with Tasks
function OrderDetailModal({ order, onClose, onUpdate }) {
  const [tasks, setTasks] = useState(order.tasks || []);
  const [loading, setLoading] = useState(false);
  const [newTaskLabel, setNewTaskLabel] = useState("");

  useEffect(() => {
    setTasks(order.tasks || []);
  }, [order]);

  const addDefaultTasks = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/wms/orders/${order.id}/tasks/add-defaults`);
      setTasks(response.data.tasks);
      toast.success(`Added ${response.data.added} default tasks`);
      onUpdate();
    } catch (error) {
      toast.error("Failed to add tasks");
    } finally {
      setLoading(false);
    }
  };

  const toggleTask = async (taskId) => {
    try {
      await axios.put(`${API_URL}/wms/orders/${order.id}/tasks/${taskId}/toggle`);
      setTasks(tasks.map((t) => (t.id === taskId ? { ...t, completed: !t.completed } : t)));
      onUpdate();
    } catch (error) {
      toast.error("Failed to update task");
    }
  };

  const addTask = async () => {
    if (!newTaskLabel.trim()) return;
    
    const newTask = {
      id: `task_${Date.now()}`,
      label: newTaskLabel.trim(),
      completed: false,
    };
    
    const updatedTasks = [...tasks, newTask];
    setTasks(updatedTasks);
    setNewTaskLabel("");
    
    try {
      await axios.put(`${API_URL}/wms/orders/${order.id}/tasks`, updatedTasks);
      onUpdate();
    } catch (error) {
      toast.error("Failed to add task");
      setTasks(tasks);
    }
  };

  const removeTask = async (taskId) => {
    const updatedTasks = tasks.filter((t) => t.id !== taskId);
    setTasks(updatedTasks);
    
    try {
      await axios.put(`${API_URL}/wms/orders/${order.id}/tasks`, updatedTasks);
      onUpdate();
    } catch (error) {
      toast.error("Failed to remove task");
      setTasks(tasks);
    }
  };

  const updateStatus = async (newStatus) => {
    try {
      await axios.put(`${API_URL}/wms/orders/${order.id}/status`, null, {
        params: { status: newStatus }
      });
      toast.success(`Status updated to ${newStatus}`);
      onUpdate();
      onClose();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const completedCount = tasks.filter((t) => t.completed).length;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="p-6 border-b">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-bold text-blue-600">{order.order_number}</h3>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                  order.status === "completed" ? "bg-green-100 text-green-700" :
                  order.status === "in_progress" ? "bg-yellow-100 text-yellow-700" :
                  "bg-blue-100 text-blue-700"
                }`}>
                  {order.status?.replace("_", " ")}
                </span>
              </div>
              <p className="text-slate-500 mt-1">{order.customer_name}</p>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Order Details */}
        <div className="p-6 border-b">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            {order.reference_number && (
              <div>
                <span className="text-slate-500">Reference</span>
                <p className="font-medium">{order.reference_number}</p>
              </div>
            )}
            {order.bl_numbers?.length > 0 && (
              <div>
                <span className="text-slate-500">BL Numbers</span>
                <p className="font-medium">{order.bl_numbers.join(", ")}</p>
              </div>
            )}
            {order.container_numbers?.length > 0 && (
              <div>
                <span className="text-slate-500">Containers</span>
                <p className="font-medium">{order.container_numbers.join(", ")}</p>
              </div>
            )}
            {order.packages && (
              <div>
                <span className="text-slate-500">Packages</span>
                <p className="font-medium">{order.packages}</p>
              </div>
            )}
            {order.gross_weight_kg && (
              <div>
                <span className="text-slate-500">Weight</span>
                <p className="font-medium">{order.gross_weight_kg} kg</p>
              </div>
            )}
            {order.volume_cbm && (
              <div>
                <span className="text-slate-500">Volume</span>
                <p className="font-medium">{order.volume_cbm} CBM</p>
              </div>
            )}
            {order.eta && (
              <div>
                <span className="text-slate-500">ETA</span>
                <p className="font-medium">{new Date(order.eta).toLocaleString()}</p>
              </div>
            )}
            {order.source_system && (
              <div>
                <span className="text-slate-500">Source</span>
                <p className="font-medium capitalize">{order.source_system}</p>
              </div>
            )}
          </div>
          {order.cargo_description && (
            <div className="mt-4">
              <span className="text-slate-500 text-sm">Cargo</span>
              <p className="font-medium">{order.cargo_description}</p>
            </div>
          )}
        </div>

        {/* Tasks Section */}
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-blue-600" />
              Tasks ({completedCount}/{tasks.length})
            </h4>
            {tasks.length === 0 && (
              <Button onClick={addDefaultTasks} size="sm" disabled={loading}>
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
                Add Default Tasks
              </Button>
            )}
          </div>

          {/* Progress Bar */}
          {tasks.length > 0 && (
            <div className="w-full bg-slate-200 rounded-full h-2 mb-4">
              <div
                className="bg-green-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(completedCount / tasks.length) * 100}%` }}
              />
            </div>
          )}

          {/* Task List */}
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {tasks.map((task) => (
              <div
                key={task.id}
                className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                  task.completed ? "bg-green-50 border-green-200" : "bg-white border-slate-200"
                }`}
              >
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => toggleTask(task.id)}
                    className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                      task.completed
                        ? "bg-green-500 border-green-500 text-white"
                        : "border-slate-300 hover:border-blue-400"
                    }`}
                  >
                    {task.completed && <CheckCircle className="w-4 h-4" />}
                  </button>
                  <div>
                    <span className={`font-medium ${task.completed ? "line-through text-slate-500" : ""}`}>
                      {task.label}
                    </span>
                    {task.description && (
                      <p className="text-xs text-slate-500">{task.description}</p>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => removeTask(task.id)}
                  className="text-slate-400 hover:text-red-500 p-1"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>

          {/* Add Task */}
          <div className="flex gap-2 mt-4">
            <Input
              value={newTaskLabel}
              onChange={(e) => setNewTaskLabel(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addTask()}
              placeholder="Add custom task..."
              className="flex-1"
            />
            <Button onClick={addTask} disabled={!newTaskLabel.trim()}>
              <Plus className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="p-6 border-t bg-slate-50 flex justify-between">
          <div className="flex gap-2">
            {order.status !== "completed" && (
              <>
                {order.status === "new" && (
                  <Button size="sm" onClick={() => updateStatus("confirmed")}>
                    Confirm Order
                  </Button>
                )}
                {order.status === "confirmed" && (
                  <Button size="sm" onClick={() => updateStatus("in_progress")}>
                    Start Processing
                  </Button>
                )}
                {order.status === "in_progress" && (
                  <Button size="sm" onClick={() => updateStatus("pending_pickup")}>
                    Ready for Pickup
                  </Button>
                )}
                {order.status === "pending_pickup" && (
                  <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => updateStatus("completed")}>
                    Mark Completed
                  </Button>
                )}
              </>
            )}
          </div>
          {order.customer_email && (
            <Button variant="outline" size="sm">
              <Mail className="w-4 h-4 mr-1" />
              Notify Customer
            </Button>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

// ==========================================
// EMAIL IMPORT TAB
// ==========================================

function EmailImportTab() {
  const [isDragging, setIsDragging] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [parsedResult, setParsedResult] = useState(null);
  const [creatingOrder, setCreatingOrder] = useState(false);
  const [recentEmails, setRecentEmails] = useState([]);

  useEffect(() => {
    fetchRecentEmailOrders();
  }, []);

  const fetchRecentEmailOrders = async () => {
    try {
      const response = await axios.get(`${API_URL}/wms/email/recent?limit=10`);
      setRecentEmails(response.data.orders || []);
    } catch (error) {
      console.error("Failed to fetch recent emails", error);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    const msgFile = files.find((f) => f.name.toLowerCase().endsWith(".msg") || f.name.toLowerCase().endsWith(".eml"));

    if (!msgFile) {
      toast.error("Please drop a .msg or .eml email file");
      return;
    }

    await parseEmailFile(msgFile);
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      await parseEmailFile(file);
    }
  };

  const parseEmailFile = async (file) => {
    setParsing(true);
    setParsedResult(null);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await axios.post(`${API_URL}/wms/email/parse`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setParsedResult(response.data);
      toast.success(`Email parsed using ${response.data.extraction_method === "ai" ? "AI" : "regex"}`);
    } catch (error) {
      toast.error("Failed to parse email: " + (error.response?.data?.detail || error.message));
    } finally {
      setParsing(false);
    }
  };

  const createOrderFromParsed = async () => {
    if (!parsedResult?.suggested_order) return;

    setCreatingOrder(true);
    try {
      const orderData = parsedResult.suggested_order;
      
      const response = await axios.post(`${API_URL}/wms/orders`, {
        ...orderData,
        bl_numbers: orderData.bl_numbers || [],
        container_numbers: orderData.container_numbers || [],
        source_system: "email",
      });

      toast.success(`Order ${response.data.order?.order_number} created!`);
      setParsedResult(null);
      fetchRecentEmailOrders();
    } catch (error) {
      toast.error("Failed to create order");
    } finally {
      setCreatingOrder(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <Mail className="w-6 h-6 text-blue-600" />
          Email Import
        </h2>
        <p className="text-sm text-slate-500 mt-1">
          Drag & drop .msg emails to create orders automatically using AI parsing
        </p>
      </div>

      {/* Drop Zone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-xl p-12 text-center transition-colors ${
          isDragging
            ? "border-blue-500 bg-blue-50"
            : "border-slate-300 hover:border-blue-400 hover:bg-slate-50"
        }`}
      >
        {parsing ? (
          <div className="flex flex-col items-center">
            <Loader2 className="w-12 h-12 text-blue-600 animate-spin mb-4" />
            <p className="font-medium text-slate-700">Parsing email with AI...</p>
            <p className="text-sm text-slate-500">Extracting order information</p>
          </div>
        ) : (
          <div className="flex flex-col items-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
              <FileUp className="w-8 h-8 text-blue-600" />
            </div>
            <p className="font-medium text-slate-700 mb-2">
              Drag & drop email files here
            </p>
            <p className="text-sm text-slate-500 mb-4">
              Supports .msg and .eml files from Outlook
            </p>
            <label className="cursor-pointer">
              <input
                type="file"
                accept=".msg,.eml"
                onChange={handleFileSelect}
                className="hidden"
              />
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                <Upload className="w-4 h-4" />
                Browse Files
              </span>
            </label>
          </div>
        )}
      </div>

      {/* Parsed Result */}
      {parsedResult && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl border shadow-sm"
        >
          {/* Email Preview */}
          <div className="p-4 border-b bg-slate-50">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-4 h-4 text-purple-600" />
              <span className="text-sm font-medium text-purple-600">
                AI Extracted ({parsedResult.extraction_method})
              </span>
            </div>
            <p className="font-medium">{parsedResult.email_preview?.subject}</p>
            <p className="text-sm text-slate-500">From: {parsedResult.email_preview?.sender}</p>
          </div>

          {/* Suggested Order Details */}
          <div className="p-4">
            <h4 className="font-semibold mb-4">Suggested Order</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-slate-500">Reference</span>
                <p className="font-medium">{parsedResult.suggested_order?.reference_number || "-"}</p>
              </div>
              <div>
                <span className="text-slate-500">Customer</span>
                <p className="font-medium">{parsedResult.suggested_order?.customer_name || "-"}</p>
              </div>
              <div>
                <span className="text-slate-500">Direction</span>
                <p className="font-medium capitalize">{parsedResult.suggested_order?.direction || "-"}</p>
              </div>
              {parsedResult.suggested_order?.container_numbers?.length > 0 && (
                <div>
                  <span className="text-slate-500">Containers</span>
                  <p className="font-medium">{parsedResult.suggested_order.container_numbers.join(", ")}</p>
                </div>
              )}
              {parsedResult.suggested_order?.packages && (
                <div>
                  <span className="text-slate-500">Packages</span>
                  <p className="font-medium">{parsedResult.suggested_order.packages}</p>
                </div>
              )}
              {parsedResult.suggested_order?.gross_weight_kg && (
                <div>
                  <span className="text-slate-500">Weight</span>
                  <p className="font-medium">{parsedResult.suggested_order.gross_weight_kg} kg</p>
                </div>
              )}
              {parsedResult.suggested_order?.truck_plate && (
                <div>
                  <span className="text-slate-500">Truck</span>
                  <p className="font-medium">{parsedResult.suggested_order.truck_plate}</p>
                </div>
              )}
              {parsedResult.suggested_order?.driver_name && (
                <div>
                  <span className="text-slate-500">Driver</span>
                  <p className="font-medium">{parsedResult.suggested_order.driver_name}</p>
                </div>
              )}
            </div>
            {parsedResult.suggested_order?.cargo_description && (
              <div className="mt-4">
                <span className="text-slate-500 text-sm">Cargo</span>
                <p className="font-medium">{parsedResult.suggested_order.cargo_description}</p>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="p-4 border-t bg-slate-50 flex justify-between">
            <Button variant="outline" onClick={() => setParsedResult(null)}>
              Cancel
            </Button>
            <div className="flex gap-2">
              <Button variant="outline">
                <Edit className="w-4 h-4 mr-2" />
                Edit Before Creating
              </Button>
              <Button onClick={createOrderFromParsed} disabled={creatingOrder}>
                {creatingOrder ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Plus className="w-4 h-4 mr-2" />
                )}
                Create Order
              </Button>
            </div>
          </div>
        </motion.div>
      )}

      {/* Recent Email Orders */}
      {recentEmails.length > 0 && (
        <div>
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <Clock className="w-5 h-5 text-slate-500" />
            Recent Orders from Email
          </h3>
          <div className="bg-white rounded-xl border divide-y">
            {recentEmails.map((order) => (
              <div key={order.id} className="p-3 hover:bg-slate-50 flex items-center justify-between">
                <div>
                  <span className="font-medium text-blue-600">{order.order_number}</span>
                  <span className="mx-2 text-slate-300">|</span>
                  <span>{order.customer_name}</span>
                  {order.container_numbers?.length > 0 && (
                    <span className="text-slate-500 ml-2 text-sm">
                      ({order.container_numbers.join(", ")})
                    </span>
                  )}
                </div>
                <span className="text-xs text-slate-400">
                  {new Date(order.created_at).toLocaleDateString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ==========================================
// SCHEDULE TAB
// ==========================================

function ScheduleTab() {
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);

  useEffect(() => {
    fetchSchedule();
  }, [selectedDate]);

  const fetchSchedule = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/wms/schedule?date=${selectedDate}`);
      setSchedule(response.data);
    } catch (error) {
      console.error("Failed to fetch schedule", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Date Selector */}
      <div className="flex items-center gap-4">
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            const prev = new Date(selectedDate);
            prev.setDate(prev.getDate() - 1);
            setSelectedDate(prev.toISOString().split("T")[0]);
          }}
        >
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <Input
          type="date"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          className="w-auto"
        />
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            const next = new Date(selectedDate);
            next.setDate(next.getDate() + 1);
            setSelectedDate(next.toISOString().split("T")[0]);
          }}
        >
          <ArrowRight className="w-4 h-4" />
        </Button>
      </div>

      {/* Schedule Grid */}
      <div className="bg-white rounded-xl border overflow-hidden">
        <div className="p-4 border-b bg-slate-50">
          <h3 className="font-semibold">Schedule for {selectedDate}</h3>
        </div>
        
        {schedule?.pre_alerts?.length > 0 ? (
          <div className="divide-y">
            {schedule.pre_alerts.map((pa) => (
              <div key={pa.id} className="p-4 flex items-center justify-between hover:bg-slate-50">
                <div className="flex items-center gap-4">
                  <div className="text-center">
                    <p className="text-lg font-bold text-blue-600">{pa.scheduled_time}</p>
                  </div>
                  <div>
                    <p className="font-medium">{pa.carrier_name}</p>
                    <p className="text-sm text-slate-500">
                      {pa.truck_plate || "No plate"} • {pa.expected_pallets || "?"} pallets
                    </p>
                    {pa.customer_name && (
                      <p className="text-xs text-slate-400">Customer: {pa.customer_name}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {pa.assigned_ramp && (
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
                      {pa.assigned_ramp}
                    </span>
                  )}
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    pa.status === "arrived" ? "bg-green-100 text-green-700" :
                    pa.status === "scheduled" ? "bg-blue-100 text-blue-700" :
                    "bg-slate-100 text-slate-700"
                  }`}>
                    {pa.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-8 text-center text-slate-400">
            <Calendar className="w-12 h-12 mx-auto mb-2 opacity-30" />
            <p>No trucks scheduled for this date</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ==========================================
// PRE-ALERTS TAB
// ==========================================

function PreAlertsTab() {
  const [preAlerts, setPreAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewForm, setShowNewForm] = useState(false);
  const [formData, setFormData] = useState({
    carrier_name: "",
    driver_name: "",
    driver_phone: "",
    truck_plate: "",
    trailer_plate: "",
    scheduled_date: new Date().toISOString().split("T")[0],
    scheduled_time: "09:00",
    direction: "inbound",
    cargo_description: "",
    expected_pallets: "",
    customer_name: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchPreAlerts();
  }, []);

  const fetchPreAlerts = async () => {
    try {
      const response = await axios.get(`${API_URL}/wms/pre-alerts`);
      setPreAlerts(response.data.pre_alerts || []);
    } catch (error) {
      console.error("Failed to fetch pre-alerts", error);
    } finally {
      setLoading(false);
    }
  };

  const createPreAlert = async (e) => {
    e.preventDefault();
    if (!formData.carrier_name) {
      toast.error("Carrier name is required");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/wms/pre-alerts`, {
        ...formData,
        expected_pallets: formData.expected_pallets ? parseInt(formData.expected_pallets) : null,
      });
      toast.success("Pre-alert created!");
      setShowNewForm(false);
      setFormData({
        carrier_name: "",
        driver_name: "",
        driver_phone: "",
        truck_plate: "",
        trailer_plate: "",
        scheduled_date: new Date().toISOString().split("T")[0],
        scheduled_time: "09:00",
        direction: "inbound",
        cargo_description: "",
        expected_pallets: "",
        customer_name: "",
        notes: "",
      });
      fetchPreAlerts();
    } catch (error) {
      toast.error("Failed to create pre-alert");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Truck Pre-Alerts</h2>
        <Button onClick={() => setShowNewForm(true)} data-testid="new-pre-alert-btn">
          <Plus className="w-4 h-4 mr-2" />
          New Pre-Alert
        </Button>
      </div>

      {/* New Pre-Alert Form Modal */}
      <AnimatePresence>
        {showNewForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6 border-b flex items-center justify-between">
                <h3 className="text-lg font-semibold">New Truck Pre-Alert</h3>
                <Button variant="ghost" size="sm" onClick={() => setShowNewForm(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              
              <form onSubmit={createPreAlert} className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">Carrier Name *</label>
                    <Input
                      value={formData.carrier_name}
                      onChange={(e) => setFormData({ ...formData, carrier_name: e.target.value })}
                      placeholder="Transport Company"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Direction</label>
                    <select
                      value={formData.direction}
                      onChange={(e) => setFormData({ ...formData, direction: e.target.value })}
                      className="w-full h-10 border rounded-md px-3"
                    >
                      <option value="inbound">Inbound (Receiving)</option>
                      <option value="outbound">Outbound (Shipping)</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">Scheduled Date *</label>
                    <Input
                      type="date"
                      value={formData.scheduled_date}
                      onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Scheduled Time *</label>
                    <Input
                      type="time"
                      value={formData.scheduled_time}
                      onChange={(e) => setFormData({ ...formData, scheduled_time: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">Driver Name</label>
                    <Input
                      value={formData.driver_name}
                      onChange={(e) => setFormData({ ...formData, driver_name: e.target.value })}
                      placeholder="John Doe"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Driver Phone</label>
                    <Input
                      value={formData.driver_phone}
                      onChange={(e) => setFormData({ ...formData, driver_phone: e.target.value })}
                      placeholder="+386..."
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">Truck Plate</label>
                    <Input
                      value={formData.truck_plate}
                      onChange={(e) => setFormData({ ...formData, truck_plate: e.target.value })}
                      placeholder="LJ-123-AB"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Trailer Plate</label>
                    <Input
                      value={formData.trailer_plate}
                      onChange={(e) => setFormData({ ...formData, trailer_plate: e.target.value })}
                      placeholder="LJ-456-CD"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">Expected Pallets</label>
                    <Input
                      type="number"
                      value={formData.expected_pallets}
                      onChange={(e) => setFormData({ ...formData, expected_pallets: e.target.value })}
                      placeholder="33"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Customer</label>
                    <Input
                      value={formData.customer_name}
                      onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                      placeholder="Customer name"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium">Cargo Description</label>
                  <Textarea
                    value={formData.cargo_description}
                    onChange={(e) => setFormData({ ...formData, cargo_description: e.target.value })}
                    placeholder="Electronics, 33 pallets, requires unloading..."
                    rows={2}
                  />
                </div>

                <div>
                  <label className="text-sm font-medium">Notes</label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Special instructions..."
                    rows={2}
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowNewForm(false)} className="flex-1">
                    Cancel
                  </Button>
                  <Button type="submit" disabled={submitting} className="flex-1">
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                    Create Pre-Alert
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Pre-Alerts List - Grouped by Day */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      ) : preAlerts.length > 0 ? (
        <PreAlertsGroupedByDay preAlerts={preAlerts} onRefresh={fetchPreAlerts} />
      ) : (
        <div className="bg-white rounded-xl border p-8 text-center">
          <Truck className="w-12 h-12 mx-auto mb-2 text-slate-300" />
          <p className="text-slate-500">No pre-alerts yet</p>
          <Button className="mt-4" onClick={() => setShowNewForm(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Create First Pre-Alert
          </Button>
        </div>
      )}
    </div>
  );
}

function PreAlertsGroupedByDay({ preAlerts, onRefresh }) {
  // Group pre-alerts by date
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  
  const dayAfter = new Date(today);
  dayAfter.setDate(dayAfter.getDate() + 2);
  
  const weekEnd = new Date(today);
  weekEnd.setDate(weekEnd.getDate() + 7);
  
  const groups = {
    overdue: { label: "Overdue", color: "red", items: [] },
    today: { label: "Today", color: "blue", items: [] },
    tomorrow: { label: "Tomorrow", color: "green", items: [] },
    thisWeek: { label: "This Week", color: "orange", items: [] },
    later: { label: "Later", color: "slate", items: [] },
  };
  
  preAlerts.forEach((pa) => {
    const paDate = new Date(pa.scheduled_date);
    paDate.setHours(0, 0, 0, 0);
    
    if (paDate < today) {
      groups.overdue.items.push(pa);
    } else if (paDate.getTime() === today.getTime()) {
      groups.today.items.push(pa);
    } else if (paDate.getTime() === tomorrow.getTime()) {
      groups.tomorrow.items.push(pa);
    } else if (paDate < weekEnd) {
      groups.thisWeek.items.push(pa);
    } else {
      groups.later.items.push(pa);
    }
  });
  
  const colorClasses = {
    red: "bg-red-50 border-red-200 text-red-700",
    blue: "bg-blue-50 border-blue-200 text-blue-700",
    green: "bg-green-50 border-green-200 text-green-700",
    orange: "bg-orange-50 border-orange-200 text-orange-700",
    slate: "bg-slate-50 border-slate-200 text-slate-700",
  };
  
  return (
    <div className="space-y-6">
      {Object.entries(groups).map(([key, group]) => (
        group.items.length > 0 && (
          <div key={key}>
            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border mb-3 ${colorClasses[group.color]}`}>
              <Calendar className="w-4 h-4" />
              <span className="font-semibold">{group.label}</span>
              <span className="text-sm opacity-70">({group.items.length})</span>
            </div>
            <div className="bg-white rounded-xl border overflow-hidden">
              <div className="divide-y">
                {group.items.map((pa) => (
                  <PreAlertRow key={pa.id} preAlert={pa} onRefresh={onRefresh} />
                ))}
              </div>
            </div>
          </div>
        )
      ))}
    </div>
  );
}

function PreAlertRow({ preAlert: pa, onRefresh }) {
  const [pinging, setPinging] = useState(false);

  const pingDriver = async () => {
    if (!pa.driver_phone) {
      toast.error("No driver phone number available");
      return;
    }

    setPinging(true);
    try {
      await axios.post(`${API_URL}/wms/drivers/ping`, {
        pre_alert_id: pa.id,
        ping_type: "eta_request",
      });
      toast.success(`ETA request sent to ${pa.driver_name || 'driver'}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to ping driver");
    } finally {
      setPinging(false);
    }
  };

  return (
    <div className="p-4 hover:bg-slate-50">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <Truck className="w-4 h-4 text-blue-600" />
            <span className="font-medium">{pa.carrier_name}</span>
            <span className={`px-2 py-0.5 rounded text-xs ${
              pa.direction === "inbound" ? "bg-green-100 text-green-700" : "bg-orange-100 text-orange-700"
            }`}>
              {pa.direction}
            </span>
          </div>
          <p className="text-sm text-slate-500 mt-1">
            {pa.scheduled_date} @ {pa.scheduled_time} • {pa.truck_plate || "No plate"} • {pa.expected_pallets || "?"} pallets
          </p>
          
          {/* Driver Info */}
          <div className="flex items-center gap-4 mt-2">
            {pa.driver_name && (
              <span className="text-xs text-slate-600 flex items-center gap-1">
                <Users className="w-3 h-3" />
                {pa.driver_name}
              </span>
            )}
            {pa.driver_phone && (
              <span className="text-xs text-slate-500 flex items-center gap-1">
                <Phone className="w-3 h-3" />
                {pa.driver_phone}
              </span>
            )}
            {pa.customer_name && (
              <span className="text-xs text-slate-400">Customer: {pa.customer_name}</span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Ping Driver Button */}
          {pa.driver_phone && pa.status !== "arrived" && pa.status !== "completed" && (
            <Button 
              variant="outline" 
              size="sm"
              onClick={pingDriver}
              disabled={pinging}
              className="text-blue-600 border-blue-200 hover:bg-blue-50"
              data-testid={`ping-driver-${pa.id}`}
            >
              {pinging ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <>
                  <Send className="w-3 h-3 mr-1" />
                  ETA
                </>
              )}
            </Button>
          )}
          
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
            pa.status === "arrived" ? "bg-green-100 text-green-700" :
            pa.status === "scheduled" ? "bg-blue-100 text-blue-700" :
            pa.status === "unloading" ? "bg-purple-100 text-purple-700" :
            pa.status === "loading" ? "bg-orange-100 text-orange-700" :
            "bg-slate-100 text-slate-700"
          }`}>
            {pa.status}
          </span>
          <Button variant="ghost" size="sm">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

// ==========================================
// OPERATIONS TAB (Driver App)
// ==========================================

function OperationsTab() {
  const [selectedOperation, setSelectedOperation] = useState(null);
  const [warehouseZones, setWarehouseZones] = useState([]);

  useEffect(() => {
    fetchWarehouseZones();
  }, []);

  const fetchWarehouseZones = async () => {
    try {
      const response = await axios.get(`${API_URL}/wms/warehouse/zones`);
      setWarehouseZones(response.data.zones || []);
    } catch (error) {
      console.error("Failed to fetch warehouse zones", error);
    }
  };

  const operations = [
    { id: "unload", label: "UNLOAD", icon: ArrowDownToLine, color: "green", description: "Receive incoming cargo" },
    { id: "load", label: "LOAD", icon: ArrowUpFromLine, color: "blue", description: "Ship outgoing cargo" },
    { id: "internal-move", label: "INTERNAL MOVE", icon: ArrowLeftRight, color: "purple", description: "Move between zones" },
    { id: "lcl-sorting", label: "LCL SORTING", icon: Layers, color: "orange", description: "Sort LCL shipments" },
    { id: "bl-photos", label: "BL PHOTOS", icon: Camera, color: "cyan", description: "Capture sub-PF photos" },
    { id: "damage", label: "DAMAGE", icon: AlertTriangle, color: "red", description: "Report damage" },
  ];

  const getColorClasses = (color) => {
    const colors = {
      green: "bg-gradient-to-br from-green-500 to-green-600",
      blue: "bg-gradient-to-br from-blue-500 to-blue-600",
      purple: "bg-gradient-to-br from-purple-500 to-purple-600",
      orange: "bg-gradient-to-br from-orange-500 to-orange-600",
      cyan: "bg-gradient-to-br from-cyan-500 to-cyan-600",
      red: "bg-gradient-to-br from-red-500 to-red-600",
    };
    return colors[color] || colors.blue;
  };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold">Warehouse Operations</h2>
      
      {/* Warehouse Zones Overview */}
      {!selectedOperation && (
        <div className="bg-white rounded-xl p-4 shadow-sm border mb-4">
          <h3 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
            <MapPin className="w-4 h-4 text-blue-600" />
            Warehouse Zones
          </h3>
          <div className="flex flex-wrap gap-2">
            {warehouseZones.filter(z => z.type === "storage").map(zone => {
              const occupancy = zone.current_count / zone.capacity;
              const bgColor = occupancy > 0.8 ? "bg-red-100 border-red-300" : 
                             occupancy > 0.5 ? "bg-yellow-100 border-yellow-300" : 
                             "bg-green-100 border-green-300";
              return (
                <span key={zone.id} className={`px-3 py-1.5 rounded-lg border text-sm font-medium ${bgColor}`}>
                  {zone.id}: {zone.current_count}/{zone.capacity}
                </span>
              );
            })}
          </div>
        </div>
      )}
      
      {!selectedOperation ? (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {operations.map((op) => (
            <motion.button
              key={op.id}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setSelectedOperation(op.id)}
              className={`p-6 rounded-2xl text-white text-center shadow-xl ${getColorClasses(op.color)}`}
              data-testid={`op-${op.id}`}
            >
              <op.icon className="w-12 h-12 mx-auto mb-3" />
              <p className="text-lg font-bold">{op.label}</p>
              <p className="text-xs opacity-80 mt-1">{op.description}</p>
            </motion.button>
          ))}
        </div>
      ) : (
        <OperationFlow
          operation={selectedOperation}
          onBack={() => setSelectedOperation(null)}
          warehouseZones={warehouseZones}
        />
      )}
    </div>
  );
}

function OperationFlow({ operation, onBack, warehouseZones = [] }) {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    shipment_id: "",
    bl_number: "",
    pallet_id: "",
    location: "",
    from_location: "",
    to_location: "",
    notes: "",
    damage_type: "",
    damage_severity: "",
    photos: [],
  });
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  const storageZones = warehouseZones.filter(z => z.type === "storage" || z.type === "ramp");

  const handlePhotoCapture = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formPayload = new FormData();
    formPayload.append("file", file);
    formPayload.append("event_type", operation);
    formPayload.append("shipment_id", formData.shipment_id || formData.bl_number);
    formPayload.append("operator_id", "driver-1");

    try {
      // For BL Photos operation, use the bl-photos endpoint
      if (operation === "bl-photos" && formData.bl_number) {
        formPayload.append("bl_number", formData.bl_number);
        formPayload.append("photo_type", "general");
        const response = await axios.post(`${API_URL}/wms/bl-photos/upload`, formPayload);
        setFormData({
          ...formData,
          photos: [...formData.photos, response.data.url],
        });
      } else {
        const response = await axios.post(`${API_URL}/wms/upload-photo`, formPayload);
        setFormData({
          ...formData,
          photos: [...formData.photos, response.data.url],
        });
      }
      toast.success("Photo uploaded!");
    } catch (error) {
      toast.error("Failed to upload photo");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const submitEvent = async () => {
    setSubmitting(true);
    try {
      // Handle Internal Move
      if (operation === "internal-move") {
        const movePayload = new FormData();
        movePayload.append("bl_number", formData.bl_number);
        movePayload.append("new_location", formData.to_location);
        movePayload.append("notes", formData.notes || "");
        movePayload.append("operator_id", "driver-1");
        
        await axios.post(`${API_URL}/wms/warehouse/move`, movePayload);
        toast.success(`Moved ${formData.bl_number} to ${formData.to_location}`);
      }
      // Handle LCL Sorting
      else if (operation === "lcl-sorting") {
        await axios.post(`${API_URL}/wms/events`, {
          event_type: "lcl-sorting",
          shipment_id: formData.shipment_id || formData.bl_number,
          location: formData.to_location,
          notes: formData.notes,
          photo_urls: formData.photos,
          operator_id: "driver-1",
        });
        toast.success("LCL Sorting completed!");
      }
      // Handle BL Photos
      else if (operation === "bl-photos") {
        toast.success(`${formData.photos.length} photo(s) uploaded for BL ${formData.bl_number}`);
      }
      // Handle other operations
      else {
        await axios.post(`${API_URL}/wms/events`, {
          event_type: operation,
          shipment_id: formData.shipment_id || "manual-entry",
          pallet_id: formData.pallet_id,
          location: formData.location || formData.to_location,
          notes: formData.notes,
          damage_type: formData.damage_type,
          damage_severity: formData.damage_severity,
          photo_urls: formData.photos,
          operator_id: "driver-1",
        });
        toast.success(`${operation.toUpperCase()} event recorded!`);
      }
      onBack();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  const getColorClasses = (op) => {
    const colors = {
      unload: "from-green-500 to-green-600",
      load: "from-blue-500 to-blue-600",
      "internal-move": "from-purple-500 to-purple-600",
      "lcl-sorting": "from-orange-500 to-orange-600",
      "bl-photos": "from-cyan-500 to-cyan-600",
      damage: "from-red-500 to-red-600",
    };
    return colors[op] || "from-blue-500 to-blue-600";
  };

  const getOperationTitle = () => {
    const titles = {
      unload: "UNLOAD",
      load: "LOAD",
      "internal-move": "INTERNAL MOVE",
      "lcl-sorting": "LCL SORTING",
      "bl-photos": "BL PHOTOS",
      damage: "DAMAGE REPORT",
    };
    return titles[operation] || operation.toUpperCase();
  };

  return (
    <div className="max-w-lg mx-auto">
      <Button variant="ghost" onClick={onBack} className="mb-4">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back
      </Button>

      <div className={`bg-gradient-to-br ${getColorClasses(operation)} text-white rounded-xl p-6 mb-6`}>
        <h3 className="text-2xl font-bold">{getOperationTitle()}</h3>
        <p className="opacity-80">Step {step} of 3</p>
      </div>

      {step === 1 && (
        <div className="bg-white rounded-xl border p-6 space-y-4">
          <h4 className="font-semibold">
            {operation === "internal-move" ? "Move Details" : 
             operation === "bl-photos" ? "BL Information" :
             operation === "lcl-sorting" ? "Sorting Details" :
             "Scan / Enter IDs"}
          </h4>
          
          {/* BL Number - for internal-move, bl-photos, lcl-sorting */}
          {(operation === "internal-move" || operation === "bl-photos" || operation === "lcl-sorting") && (
            <div>
              <label className="text-sm font-medium text-slate-600 mb-1 block">BL Number *</label>
              <Input
                value={formData.bl_number}
                onChange={(e) => setFormData({ ...formData, bl_number: e.target.value.toUpperCase() })}
                placeholder="Enter BL number"
                className="font-mono"
              />
            </div>
          )}

          {/* Zone Selection for internal-move */}
          {operation === "internal-move" && (
            <div>
              <label className="text-sm font-medium text-slate-600 mb-1 block">Move to Zone *</label>
              <div className="grid grid-cols-4 gap-2">
                {storageZones.map(zone => {
                  const occupancy = zone.capacity > 0 ? zone.current_count / zone.capacity : 0;
                  const isSelected = formData.to_location === zone.id;
                  const bgColor = isSelected ? "bg-purple-500 text-white border-purple-600" :
                                 occupancy > 0.8 ? "bg-red-100 border-red-300 text-red-700" : 
                                 occupancy > 0.5 ? "bg-yellow-100 border-yellow-300 text-yellow-700" : 
                                 "bg-green-100 border-green-300 text-green-700";
                  return (
                    <button
                      key={zone.id}
                      onClick={() => setFormData({ ...formData, to_location: zone.id })}
                      className={`p-2 rounded-lg border text-center font-medium transition-colors ${bgColor}`}
                    >
                      {zone.id}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Zone Selection for lcl-sorting */}
          {operation === "lcl-sorting" && (
            <div>
              <label className="text-sm font-medium text-slate-600 mb-1 block">Sort to Zone</label>
              <div className="grid grid-cols-4 gap-2">
                {storageZones.map(zone => (
                  <button
                    key={zone.id}
                    onClick={() => setFormData({ ...formData, to_location: zone.id })}
                    className={`p-2 rounded-lg border text-center font-medium transition-colors ${
                      formData.to_location === zone.id ? "bg-orange-500 text-white" : "bg-slate-50 hover:bg-slate-100"
                    }`}
                  >
                    {zone.id}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Shipment ID - for unload, load */}
          {(operation === "unload" || operation === "load" || operation === "damage") && (
            <>
              <div>
                <label className="text-sm font-medium">Shipment / Truck ID</label>
                <Input
                  value={formData.shipment_id}
                  onChange={(e) => setFormData({ ...formData, shipment_id: e.target.value })}
                  placeholder="Scan or enter ID"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Pallet ID (SSCC)</label>
                <Input
                  value={formData.pallet_id}
                  onChange={(e) => setFormData({ ...formData, pallet_id: e.target.value })}
                  placeholder="Scan pallet barcode"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Warehouse Zone</label>
                <div className="grid grid-cols-4 gap-2 mt-1">
                  {storageZones.length > 0 ? storageZones.map(zone => (
                    <button
                      key={zone.id}
                      onClick={() => setFormData({ ...formData, location: zone.id })}
                      className={`p-2 rounded-lg border text-center font-medium transition-colors ${
                        formData.location === zone.id ? "bg-blue-500 text-white" : "bg-slate-50 hover:bg-slate-100"
                      }`}
                    >
                      {zone.id}
                    </button>
                  )) : (
                    <select
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      className="col-span-4 w-full h-10 border rounded-md px-3"
                    >
                      <option value="">Select Zone...</option>
                      <option value="RAMPA">RAMPA</option>
                      <option value="A1">A1</option>
                      <option value="A2">A2</option>
                      <option value="B1">B1</option>
                      <option value="B2">B2</option>
                      <option value="C1">C1</option>
                    </select>
                  )}
                </div>
              </div>
            </>
          )}
          <Button className="w-full" onClick={() => setStep(2)}>
            Next: Take Photos
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      )}

      {step === 2 && (
        <div className="bg-white rounded-xl border p-6 space-y-4">
          <h4 className="font-semibold">Take Photos</h4>
          
          <div className="grid grid-cols-3 gap-2">
            {formData.photos.map((url, idx) => (
              <img key={idx} src={`${API_URL}${url}`} alt={`Photo ${idx + 1}`} className="w-full h-24 object-cover rounded-lg" />
            ))}
          </div>

          <label className={`block w-full p-8 border-2 border-dashed rounded-xl text-center cursor-pointer ${
            uploading ? "opacity-50" : "hover:border-blue-400"
          }`}>
            <input
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handlePhotoCapture}
              disabled={uploading}
              className="hidden"
            />
            {uploading ? (
              <Loader2 className="w-8 h-8 mx-auto animate-spin text-blue-600" />
            ) : (
              <>
                <Camera className="w-12 h-12 mx-auto text-slate-400 mb-2" />
                <p className="text-slate-600">Tap to take photo</p>
              </>
            )}
          </label>

          {operation === "damage" && (
            <>
              <div>
                <label className="text-sm font-medium">Damage Type</label>
                <select
                  value={formData.damage_type}
                  onChange={(e) => setFormData({ ...formData, damage_type: e.target.value })}
                  className="w-full h-10 border rounded-md px-3"
                >
                  <option value="">Select damage type</option>
                  <option value="crushed">Crushed</option>
                  <option value="torn_wrap">Torn Wrap</option>
                  <option value="water_damage">Water Damage</option>
                  <option value="broken_pallet">Broken Pallet</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium">Severity</label>
                <select
                  value={formData.damage_severity}
                  onChange={(e) => setFormData({ ...formData, damage_severity: e.target.value })}
                  className="w-full h-10 border rounded-md px-3"
                >
                  <option value="">Select severity</option>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
            </>
          )}

          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setStep(1)} className="flex-1">
              Back
            </Button>
            <Button onClick={() => setStep(3)} className="flex-1">
              Next: Review
            </Button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="bg-white rounded-xl border p-6 space-y-4">
          <h4 className="font-semibold">Review & Submit</h4>
          
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-500">Operation:</span>
              <span className="font-medium">{operation.toUpperCase()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Shipment:</span>
              <span className="font-medium">{formData.shipment_id || "N/A"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Pallet:</span>
              <span className="font-medium">{formData.pallet_id || "N/A"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Location:</span>
              <span className="font-medium">{formData.location || "N/A"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Photos:</span>
              <span className="font-medium">{formData.photos.length}</span>
            </div>
            {operation === "damage" && (
              <>
                <div className="flex justify-between">
                  <span className="text-slate-500">Damage Type:</span>
                  <span className="font-medium">{formData.damage_type || "N/A"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Severity:</span>
                  <span className="font-medium">{formData.damage_severity || "N/A"}</span>
                </div>
              </>
            )}
          </div>

          <div>
            <label className="text-sm font-medium">Notes</label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Additional notes..."
              rows={2}
            />
          </div>

          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setStep(2)} className="flex-1">
              Back
            </Button>
            <Button 
              onClick={submitEvent} 
              disabled={submitting}
              className={`flex-1 ${
                colorClass === "green" ? "bg-green-600 hover:bg-green-700" :
                colorClass === "blue" ? "bg-blue-600 hover:bg-blue-700" :
                "bg-red-600 hover:bg-red-700"
              }`}
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <CheckCircle className="w-4 h-4 mr-2" />
              )}
              Submit
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ==========================================
// INVENTORY TAB
// ==========================================

function InventoryTab() {
  return (
    <div className="bg-white rounded-xl border p-8 text-center">
      <Package className="w-12 h-12 mx-auto mb-2 text-slate-300" />
      <h3 className="font-semibold text-slate-700">Inventory Management</h3>
      <p className="text-slate-500 text-sm mt-1">Coming soon - Zone setup required</p>
    </div>
  );
}

// ==========================================
// CUSTOMER PORTAL TAB
// ==========================================

function CustomerPortalTab() {
  const [searchRef, setSearchRef] = useState("");
  const [shipment, setShipment] = useState(null);
  const [loading, setLoading] = useState(false);

  const searchShipment = async () => {
    if (!searchRef.trim()) return;
    
    setLoading(true);
    try {
      // For demo, search by customer name
      const response = await axios.get(`${API_URL}/wms/customer/shipments?customer_id=${searchRef}`);
      if (response.data.shipments?.length > 0) {
        setShipment(response.data.shipments[0]);
      } else {
        toast.error("No shipments found");
      }
    } catch (error) {
      toast.error("Search failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl border p-6">
        <h3 className="font-semibold mb-4">Customer Shipment Tracking</h3>
        <div className="flex gap-3">
          <Input
            value={searchRef}
            onChange={(e) => setSearchRef(e.target.value)}
            placeholder="Enter reference number or customer ID"
            className="flex-1"
          />
          <Button onClick={searchShipment} disabled={loading}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      {shipment && (
        <div className="bg-white rounded-xl border p-6">
          <h4 className="font-semibold mb-4">Shipment Details</h4>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-slate-500">Carrier:</span>
                <p className="font-medium">{shipment.carrier_name}</p>
              </div>
              <div>
                <span className="text-slate-500">Status:</span>
                <p className="font-medium capitalize">{shipment.status}</p>
              </div>
              <div>
                <span className="text-slate-500">Scheduled:</span>
                <p className="font-medium">{shipment.scheduled_date} {shipment.scheduled_time}</p>
              </div>
              <div>
                <span className="text-slate-500">Pallets:</span>
                <p className="font-medium">{shipment.expected_pallets || "N/A"}</p>
              </div>
            </div>

            {shipment.events?.length > 0 && (
              <div>
                <h5 className="font-medium mb-2">Activity Timeline</h5>
                <div className="space-y-2">
                  {shipment.events.map((event, idx) => (
                    <div key={idx} className="flex gap-3 text-sm">
                      <div className="w-2 h-2 rounded-full bg-blue-500 mt-2" />
                      <div>
                        <p className="font-medium capitalize">{event.event_type}</p>
                        <p className="text-slate-500 text-xs">{new Date(event.timestamp).toLocaleString()}</p>
                        {event.photo_urls?.length > 0 && (
                          <div className="flex gap-1 mt-1">
                            {event.photo_urls.map((url, i) => (
                              <img key={i} src={`${API_URL}${url}`} alt="" className="w-12 h-12 object-cover rounded" />
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
